import {
    Column,
    DataTable,
    logger as splatTransformLogger,
    MemoryFileSystem,
    Transform,
    WebPCodec,
    WorkerQueue,
    writeHtml,
    writeSog as writeSogInternal,
    ZipFileSystem,
    type FileSystem,
    type LogEvent,
    type Renderer,
    type Writer
} from '@playcanvas/splat-transform';
import {
    GSplatData,
    Mat3,
    Mat4,
    PIXELFORMAT_BGRA8,
    Quat,
    Texture,
    Vec3,
    WebgpuGraphicsDevice
} from 'playcanvas';

import { version } from '../package.json';
import { ColorGrade, dcDecode, dcEncode, sigmoid } from './color-grade';
import { Events } from './events';
import { BrowserFileSystem, dataTableToGSplatData, loadLodDataTable, ProgressWriter } from './io';
import { SHRotation } from './sh-utils';
import { Splat } from './splat';
import { State } from './splat-state';

type SerializeSettings = {
    maxSHBands?: number;            // specifies the maximum number of bands to be exported
    selected?: boolean;             // only export selected gaussians. used for copy/paste
    minOpacity?: number;            // filter out gaussians with alpha less than or equal to minAlpha
    removeInvalid?: boolean;        // filter out gaussians with invalid data (NaN/Infinity)

    // the following options are used when serializing the PLY for document save
    // and are only supported by serializePly
    keepStateData?: boolean;        // keep the state data array
    preserveDeleted?: boolean;      // keep deleted gaussians in vertex data (for index consistency)
    keepWorldTransform?: boolean;   // don't apply the world transform when resolving splat transforms
    keepColorTint?: boolean;        // refrain from applying color tints
    skipPlyRotation?: boolean;      // skip the 180° Z rotation for PLY coordinate system (used for in-memory merge)
};

type AnimTrack = {
    name: string,
    duration: number,
    frameRate: number,
    loopMode: 'none' | 'repeat' | 'pingpong',
    interpolation: 'step' | 'spline',
    smoothness: number,
    keyframes: {
        times: number[],
        values: {
            position: number[],
            target: number[],
            fov: number[],
        }
    }
};

type CameraPose = {
    position: [number, number, number],
    target: [number, number, number],
    fov: number
};

type Camera = {
    initial: CameraPose,
};

type Annotation = {
    position: [number, number, number],
    title: string,
    text: string,
    extras: any,
    camera: Camera
};

type PostEffectSettings = {
    sharpness: {
        enabled: boolean,
        amount: number,
    },
    bloom: {
        enabled: boolean,
        intensity: number,
        blurLevel: number,
    },
    grading: {
        enabled: boolean,
        brightness: number,
        contrast: number,
        saturation: number,
        tint: [number, number, number],
    },
    vignette: {
        enabled: boolean,
        intensity: number,
        inner: number,
        outer: number,
        curvature: number,
    },
    fringing: {
        enabled: boolean,
        intensity: number
    }
};

const defaultPostEffectSettings: PostEffectSettings = {
    sharpness: { enabled: false, amount: 0 },
    bloom: { enabled: false, intensity: 1, blurLevel: 2 },
    grading: { enabled: false, brightness: 1, contrast: 1, saturation: 1, tint: [1, 1, 1] },
    vignette: { enabled: false, intensity: 0.5, inner: 0.3, outer: 0.75, curvature: 1 },
    fringing: { enabled: false, intensity: 0.5 }
};

type ExperienceSettings = {
    version: 2,
    tonemapping: 'none' | 'linear' | 'filmic' | 'hejl' | 'aces' | 'aces2' | 'neutral',
    highPrecisionRendering: boolean,
    soundUrl?: string,
    background: {
        color: [number, number, number],
        skyboxUrl?: string
    },
    postEffectSettings: PostEffectSettings,
    animTracks: AnimTrack[],
    cameras: Camera[],
    annotations: Annotation[],
    startMode: 'default' | 'animTrack' | 'annotation'
};

type ViewerExportSettings = {
    type: 'html' | 'zip';
    experienceSettings: ExperienceSettings;
    events?: Events;
};

type ProgressFunc = (loaded: number, total: number) => void;

const generatedByString = `Generated by ReSplat ${version}`;

// create a filter for gaussians
class GaussianFilter {
    set: (splat: Splat) => void;
    test: (i: number) => boolean;

    constructor(serializeSettings: SerializeSettings) {
        let splat: Splat = null;
        let state: Uint8Array = null;
        let opacity: Float32Array = null;

        this.set = (s: Splat) => {
            splat = s;
            state = splat.splatData.getProp('state') as Uint8Array;
            opacity = splat.splatData.getProp('opacity') as Float32Array;
        };

        const onlySelected = serializeSettings.selected ?? false;
        const minOpacity = serializeSettings.minOpacity ?? 0;
        const removeInvalid = serializeSettings.removeInvalid ?? false;
        const keepStateData = serializeSettings.keepStateData ?? false;
        const preserveDeleted = serializeSettings.preserveDeleted ?? false;

        // properties where +Infinity and -Infinity are valid values
        const infOk = new Set(['opacity']);
        // properties where -Infinity is a valid value
        const negInfOk = new Set(['scale_0', 'scale_1', 'scale_2']);

        this.test = (i: number) => {
            // splat is deleted, skip unless preserving state data (for index validity)
            // or explicitly preserving deleted gaussians
            if (!keepStateData && !preserveDeleted && (state[i] & State.deleted) !== 0) {
                return false;
            }

            // optionally filter out unselected gaussians
            if (onlySelected && (state[i] !== State.selected)) {
                return false;
            }

            // optionally filter based on opacity
            if (minOpacity > 0 && sigmoid(opacity[i]) < minOpacity) {
                return false;
            }

            if (removeInvalid) {
                const { splatData } = splat;

                // check if any property of the gaussian is NaN/Infinity
                const element = splatData.getElement('vertex');
                for (let k = 0; k < element.properties.length; ++k) {
                    const prop = element.properties[k];
                    const { storage, name } = prop;
                    if (storage && !Number.isFinite(storage[i])) {
                        if (storage[i] === -Infinity && (infOk.has(name) || negInfOk.has(name))) continue;
                        if (storage[i] === Infinity && infOk.has(name)) continue;
                        return false;
                    }
                }
            }

            return true;
        };
    }
}

// count the total number of gaussians given a filter
const countGaussians = (splats: Splat[], filter: GaussianFilter) => {
    return splats.reduce((accum, splat) => {
        filter.set(splat);
        for (let i = 0; i < splat.splatData.numSplats; ++i) {
            accum += filter.test(i) ? 1 : 0;
        }
        return accum;
    }, 0);
};

const getVertexProperties = (splatData: GSplatData) => {
    return new Set<string>(
        splatData.getElement('vertex')
        .properties.filter((p: any) => p.storage)
        .map((p: any) => p.name)
    );
};

const getCommonPropNames = (splats: Splat[]) => {
    let result: Set<string>;

    for (let i = 0; i < splats.length; ++i) {
        const props = getVertexProperties(splats[i].splatData);
        result = i === 0 ? props : new Set([...result].filter(i => props.has(i)));
    }

    return [...result];
};

const getCommonProps = (splats: Splat[]) => {
    const result = new Map<string, Set<string>>();  // map of name->type

    for (let i = 0; i < splats.length; ++i) {
        const properties = splats[i].splatData.getElement('vertex').properties.filter((p: any) => p.storage);
        properties.forEach((p: any) => {
            if (result.has(p.name)) {
                result.get(p.name).add(p.type);
            } else {
                result.set(p.name, new Set([p.type]));
            }
        });
    }

    return [...result].filter(([_, v]) => v.size === 1).map(([name, type]) => {
        return { name, type: type.values().next().value };
    });
};

const shNames = new Array(45).fill('').map((_, i) => `f_rest_${i}`);
const shBandCoeffs = [0, 3, 8, 15];

// determine the number of sh bands present given an object with 'f_rest_*' properties
const calcSHBands = (data: Set<string>) => {
    return { '9': 1, '24': 2, '-1': 3 }[shNames.findIndex(v => !data.has(v))] ?? 0;
};

type DataType = 'char' | 'uchar' | 'short' | 'ushort' | 'int' | 'uint' | 'float' | 'double';

const DataTypeSize = (dataType: DataType) => {
    return {
        char: 1,
        uchar: 1,
        short: 2,
        ushort: 2,
        int: 4,
        uint: 4,
        float: 4,
        double: 8
    }[dataType];
};

const v = new Vec3();
const q = new Quat();

// calculate splat transforms on demand and cache the result for next time
class SplatTransformCache {
    getMat: (index: number) => Mat4;
    getRot: (index: number) => Quat;
    getScale: (index: number) => Vec3;
    getSHRot: (index: number) => SHRotation;

    constructor(splat: Splat, keepWorldTransform = false, skipPlyRotation = false) {
        const transforms = new Map<number, { transformIndex: number, mat: Mat4, rot: Quat, scale: Vec3, shRot: SHRotation }>();
        const indices = splat.transformTexture.getSource() as unknown as Uint32Array;
        const tmpMat = new Mat4();
        const tmpMat3 = new Mat3();
        const tmpQuat = new Quat();

        const getTransform = (index: number) => {
            const transformIndex = indices?.[index] ?? 0;
            let result = transforms.get(transformIndex);
            if (!result) {
                result = { transformIndex, mat: null, rot: null, scale: null, shRot: null };
                transforms.set(transformIndex, result);
            }
            return result;
        };

        this.getMat = (index: number) => {
            const transform = getTransform(index);

            if (!transform.mat) {
                const mat = new Mat4();

                // we must undo the transform we apply at load time to output data
                if (!keepWorldTransform) {
                    if (!skipPlyRotation) {
                        mat.setFromEulerAngles(0, 0, -180);
                    }
                    mat.mul2(mat, splat.entity.getWorldTransform());
                }

                // combine with transform palette matrix
                if (transform.transformIndex > 0) {
                    splat.transformPalette.getTransform(transform.transformIndex, tmpMat);
                    mat.mul2(mat, tmpMat);
                }

                transform.mat = mat;
            }

            return transform.mat;
        };

        this.getRot = (index: number) => {
            const transform = getTransform(index);

            if (!transform.rot) {
                transform.rot = new Quat().setFromMat4(this.getMat(index));
            }

            return transform.rot;
        };

        this.getScale = (index: number) => {
            const transform = getTransform(index);

            if (!transform.scale) {
                const scale = new Vec3();
                this.getMat(index).getScale(scale);
                transform.scale = scale;
            }

            return transform.scale;
        };

        this.getSHRot = (index: number) => {
            const transform = getTransform(index);

            if (!transform.shRot) {
                tmpQuat.setFromMat4(this.getMat(index));
                tmpMat3.setFromQuat(tmpQuat);
                transform.shRot = new SHRotation(tmpMat3);
            }

            return transform.shRot;
        };
    }
}

// helper class for extracting and transforming a single splat's data
// to prepare it for export
class SingleSplat {
    // final data keyed on member name
    data: any = {};

    // read a single gaussian's data and transform it for export
    read: (splats: Splat, i: number) => void;

    // specify the data members required
    constructor(members: string[], serializeSettings: SerializeSettings) {
        const data: any = {};
        members.forEach((name) => {
            data[name] = 0;
        });

        const hasPosition = ['x', 'y', 'z'].every(v => data.hasOwnProperty(v));
        const hasRotation = ['rot_0', 'rot_1', 'rot_2', 'rot_3'].every(v => data.hasOwnProperty(v));
        const hasScale = ['scale_0', 'scale_1', 'scale_2'].every(v => data.hasOwnProperty(v));
        const hasColor = ['f_dc_0', 'f_dc_1', 'f_dc_2'].every(v => data.hasOwnProperty(v));
        const hasOpacity = data.hasOwnProperty('opacity');

        const dstSHBands = calcSHBands(new Set(Object.keys(data)));
        const dstSHCoeffs = shBandCoeffs[dstSHBands];
        const tmpSHData = dstSHBands ? new Float32Array(dstSHCoeffs) : null;

        type CacheEntry = {
            splat: Splat;
            transformCache: SplatTransformCache;
            srcProps: { [name: string]: Float32Array };
            grade: ColorGrade;
        };

        const cacheMap = new Map<Splat, CacheEntry>();
        let cacheEntry: CacheEntry;

        const read = (splat: Splat, i: number) => {
            // get the cached data entry for this splat
            if (splat !== cacheEntry?.splat) {
                if (!cacheMap.has(splat)) {
                    const transformCache = new SplatTransformCache(splat, serializeSettings.keepWorldTransform, serializeSettings.skipPlyRotation);

                    const srcPropNames = getVertexProperties(splat.splatData);
                    const srcSHBands = calcSHBands(srcPropNames);
                    const srcSHCoeffs = shBandCoeffs[srcSHBands];

                    // cache the props objects
                    const srcProps: { [name: string]: Float32Array } = {};

                    members.forEach((name) => {
                        const shIndex = shNames.indexOf(name);
                        if (shIndex >= 0) {
                            const a = Math.floor(shIndex / dstSHCoeffs);
                            const b = shIndex % dstSHCoeffs;
                            srcProps[name] = (b < srcSHCoeffs) ? splat.splatData.getProp(shNames[a * srcSHCoeffs + b]) as Float32Array : null;
                        } else {
                            srcProps[name] = splat.splatData.getProp(name) as Float32Array;
                        }
                    });

                    const grade = new ColorGrade(splat);

                    cacheEntry = { splat, transformCache, srcProps, grade };

                    cacheMap.set(splat, cacheEntry);
                } else {
                    cacheEntry = cacheMap.get(splat);
                }
            }

            const { transformCache, srcProps, grade } = cacheEntry;

            // copy members
            members.forEach((name) => {
                data[name] = srcProps[name]?.[i] ?? 0;
            });

            // apply transform palette transforms
            const mat = transformCache.getMat(i);

            if (hasPosition) {
                v.set(data.x, data.y, data.z);
                mat.transformPoint(v, v);
                [data.x, data.y, data.z] = [v.x, v.y, v.z];
            }

            if (hasRotation) {
                const quat = transformCache.getRot(i);
                q.set(data.rot_1, data.rot_2, data.rot_3, data.rot_0).mul2(quat, q);
                [data.rot_1, data.rot_2, data.rot_3, data.rot_0] = [q.x, q.y, q.z, q.w];
            }

            if (hasScale) {
                const scale = transformCache.getScale(i);
                data.scale_0 = Math.log(Math.exp(data.scale_0) * scale.x);
                data.scale_1 = Math.log(Math.exp(data.scale_1) * scale.y);
                data.scale_2 = Math.log(Math.exp(data.scale_2) * scale.z);
            }

            if (dstSHBands > 0) {
                for (let c = 0; c < 3; ++c) {
                    for (let d = 0; d < dstSHCoeffs; ++d) {
                        tmpSHData[d] = data[shNames[c * dstSHCoeffs + d]];
                    }

                    transformCache.getSHRot(i).apply(tmpSHData);

                    for (let d = 0; d < dstSHCoeffs; ++d) {
                        data[shNames[c * dstSHCoeffs + d]] = tmpSHData[d];
                    }
                }
            }

            if (!serializeSettings.keepColorTint && hasColor && grade.hasTint) {
                const c = {
                    r: dcDecode(data.f_dc_0),
                    g: dcDecode(data.f_dc_1),
                    b: dcDecode(data.f_dc_2)
                };

                grade.applyDC(c);
                data.f_dc_0 = dcEncode(c.r);
                data.f_dc_1 = dcEncode(c.g);
                data.f_dc_2 = dcEncode(c.b);

                if (dstSHBands > 0) {
                    for (let d = 0; d < dstSHCoeffs; ++d) {
                        c.r = data[shNames[d]];
                        c.g = data[shNames[d + dstSHCoeffs]];
                        c.b = data[shNames[d + dstSHCoeffs * 2]];

                        grade.applySH(c);
                        data[shNames[d]] = c.r;
                        data[shNames[d + dstSHCoeffs]] = c.g;
                        data[shNames[d + dstSHCoeffs * 2]] = c.b;
                    }
                }
            }

            if (!serializeSettings.keepColorTint && hasOpacity && splat.transparency !== 1) {
                data.opacity = grade.applyOpacity(data.opacity);
            }
        };

        this.data = data;
        this.read = read;
    }
}

const serializePlyToWriter = async (splats: Splat[], serializeSettings: SerializeSettings, writer: Writer, progress?: ProgressFunc): Promise<void> => {
    const { maxSHBands, keepStateData } = serializeSettings;

    // create filter and count total gaussians
    const filter = new GaussianFilter(serializeSettings);
    const totalGaussians = countGaussians(splats, filter);
    if (totalGaussians === 0) {
        return;
    }

    // this data is filtered out, as it holds internal editor state
    const internalProps = keepStateData ? ['transform'] : ['state', 'transform'];

    const props = getCommonProps(splats)
    // filter out internal props
    .filter(p => !internalProps.includes(p.name))
    // filter out max SH bands
    .filter((p) => {
        if (!p.name.startsWith('f_rest_')) {
            return true;
        }
        const i = parseInt(p.name.slice(7), 10);
        return i < [0, 9, 24, 45][maxSHBands ?? 3];
    });

    const headerText = [
        'ply',
        'format binary_little_endian 1.0',
        // FIXME: disable for now due to other tooling not supporting any header
        // `comment ${generatedByString}`,
        `element vertex ${totalGaussians}`,
        props.map(p => `property ${p.type} ${p.name}`),
        'end_header',
        ''
    ].flat().join('\n');

    const singleSplat = new SingleSplat(props.map(p => p.name), serializeSettings);

    const gaussianSizeBytes = props.reduce((tot, p) => tot + DataTypeSize(p.type as DataType), 0);

    const buf = new Uint8Array(1024 * gaussianSizeBytes);
    const dataView = new DataView(buf.buffer);
    let offset = 0;

    const header = new TextEncoder().encode(headerText);

    // construct a progress writer over the writer
    const progressWriter = new ProgressWriter(writer, header.byteLength + totalGaussians * gaussianSizeBytes, progress);

    // write encoded header
    await progressWriter.write(header);

    for (let e = 0; e < splats.length; ++e) {
        const splat = splats[e];
        const { splatData } = splat;
        filter.set(splat);

        for (let i = 0; i < splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            // write
            for (let j = 0; j < props.length; ++j) {
                if (props[j].type === 'uchar') {
                    dataView.setUint8(offset, singleSplat.data[props[j].name]);
                    offset += 1;
                } else {
                    dataView.setFloat32(offset, singleSplat.data[props[j].name], true);
                    offset += 4;
                }
            }

            // buffer is full, write it to the output stream
            if (offset === buf.byteLength) {
                await progressWriter.write(buf);
                offset = 0;
            }
        }
    }

    // write the last (most likely partially filled) buf
    if (offset > 0) {
        await progressWriter.write(new Uint8Array(buf.buffer, 0, offset));
    }

    progressWriter.close();
};

const serializePly = async (splats: Splat[], serializeSettings: SerializeSettings, fs: FileSystem, filename = 'output.ply', progress?: ProgressFunc): Promise<void> => {
    const writer = await fs.createWriter(filename);
    await serializePlyToWriter(splats, serializeSettings, writer, progress);
    await writer.close();
};

// Standard PLY format: 62 properties in a specific order
// This format is compatible with most Gaussian Splat renderers
const STANDARD_PLY_PROPERTIES = [
    // Position (3)
    'x', 'y', 'z',
    // Normals (3) - filled with 0 if missing
    'nx', 'ny', 'nz',
    // Spherical Harmonics DC (3)
    'f_dc_0', 'f_dc_1', 'f_dc_2',
    // Spherical Harmonics Rest (45) - filled with 0 if missing
    ...new Array(45).fill('').map((_, i) => `f_rest_${i}`),
    // Opacity (1)
    'opacity',
    // Scale (3)
    'scale_0', 'scale_1', 'scale_2',
    // Rotation (4)
    'rot_0', 'rot_1', 'rot_2', 'rot_3'
];

const serializeStandardPly = async (splats: Splat[], serializeSettings: SerializeSettings, fs: FileSystem, filename = 'output.ply', progress?: ProgressFunc): Promise<void> => {
    const { maxSHBands } = serializeSettings;

    // create filter and count total gaussians
    const filter = new GaussianFilter(serializeSettings);
    const totalGaussians = countGaussians(splats, filter);
    if (totalGaussians === 0) {
        return;
    }

    // Determine which f_rest properties to include based on maxSHBands
    const maxSHCoeffs = [0, 9, 24, 45][maxSHBands ?? 3];

    // Build properties list - always include all standard properties
    // but respect maxSHBands for f_rest
    const props: { name: string; type: string }[] = [];
    for (const name of STANDARD_PLY_PROPERTIES) {
        // Check if this is an f_rest property beyond our band limit
        if (name.startsWith('f_rest_')) {
            const idx = parseInt(name.slice(7), 10);
            if (idx >= maxSHCoeffs) {
                continue; // Skip f_rest beyond selected bands
            }
        }
        props.push({ name, type: 'float' });
    }

    // Build header
    const headerText = [
        'ply',
        'format binary_little_endian 1.0',
        `element vertex ${totalGaussians}`,
        props.map(p => `property ${p.type} ${p.name}`),
        'end_header',
        ''
    ].flat().join('\n');

    // Get the property names that exist in the source data
    const commonPropNames = new Set(getCommonPropNames(splats));

    // Create SingleSplat reader for properties that exist in source
    // We need to request all properties we want to read
    const propsToRead = props.map(p => p.name).filter((name) => {
        // Always try to read these core properties
        if (['x', 'y', 'z', 'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
            'scale_0', 'scale_1', 'scale_2',
            'rot_0', 'rot_1', 'rot_2', 'rot_3'].includes(name)) {
            return true;
        }
        // For normals and f_rest, only read if they exist
        return commonPropNames.has(name);
    });

    const singleSplat = new SingleSplat(propsToRead, serializeSettings);

    const gaussianSizeBytes = props.length * 4; // all properties are float32

    const buf = new Uint8Array(1024 * gaussianSizeBytes);
    const dataView = new DataView(buf.buffer);
    let offset = 0;

    const header = new TextEncoder().encode(headerText);

    // create writer from filesystem
    const writer = await fs.createWriter(filename);

    // construct a progress writer over the writer
    const progressWriter = new ProgressWriter(writer, header.byteLength + totalGaussians * gaussianSizeBytes, progress);

    // write encoded header
    await progressWriter.write(header);

    for (let e = 0; e < splats.length; ++e) {
        const splat = splats[e];
        const { splatData } = splat;
        filter.set(splat);

        for (let i = 0; i < splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            // write all properties in standard order
            for (let j = 0; j < props.length; ++j) {
                const propName = props[j].name;
                // Use value from source if available, otherwise 0
                const value = Object.prototype.hasOwnProperty.call(singleSplat.data, propName) ? singleSplat.data[propName] : 0;
                dataView.setFloat32(offset, value, true);
                offset += 4;
            }

            // buffer is full, write it to the output stream
            if (offset === buf.byteLength) {
                await progressWriter.write(buf);
                offset = 0;
            }
        }
    }

    // write the last (most likely partially filled) buf
    if (offset > 0) {
        await progressWriter.write(new Uint8Array(buf.buffer, 0, offset));
    }

    progressWriter.close();
    await writer.close();
};

interface CompressedIndex {
    splatIndex: number;
    i: number;
    globalIndex: number;
}

// process and compress a chunk of 256 splats
class Chunk {
    static members = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3'
    ];

    size: number;
    data: any = {};

    // compressed data
    position: Uint32Array;
    rotation: Uint32Array;
    scale: Uint32Array;
    color: Uint32Array;

    constructor(size = 256) {
        this.size = size;
        Chunk.members.forEach((m) => {
            this.data[m] = new Float32Array(size);
        });
        this.position = new Uint32Array(size);
        this.rotation = new Uint32Array(size);
        this.scale = new Uint32Array(size);
        this.color = new Uint32Array(size);
    }

    set(index: number, splat: SingleSplat) {
        Chunk.members.forEach((name) => {
            this.data[name][index] = splat.data[name];
        });
    }

    pack() {
        const calcMinMax = (data: Float32Array) => {
            let min;
            let max;
            min = max = data[0];
            for (let i = 1; i < data.length; ++i) {
                const v = data[i];
                min = Math.min(min, v);
                max = Math.max(max, v);
            }
            return { min, max };
        };

        const normalize = (x: number, min: number, max: number) => {
            if (x <= min) return 0;
            if (x >= max) return 1;
            return (max - min < 0.00001) ? 0 : (x - min) / (max - min);
        };

        const data = this.data;

        const x = data.x;
        const y = data.y;
        const z = data.z;
        const scale_0 = data.scale_0;
        const scale_1 = data.scale_1;
        const scale_2 = data.scale_2;
        const rot_0 = data.rot_0;
        const rot_1 = data.rot_1;
        const rot_2 = data.rot_2;
        const rot_3 = data.rot_3;
        const f_dc_0 = data.f_dc_0;
        const f_dc_1 = data.f_dc_1;
        const f_dc_2 = data.f_dc_2;
        const opacity = data.opacity;

        const px = calcMinMax(x);
        const py = calcMinMax(y);
        const pz = calcMinMax(z);

        const sx = calcMinMax(scale_0);
        const sy = calcMinMax(scale_1);
        const sz = calcMinMax(scale_2);

        // clamp scale because sometimes values are at infinity
        const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
        sx.min = clamp(sx.min, -20, 20);
        sx.max = clamp(sx.max, -20, 20);
        sy.min = clamp(sy.min, -20, 20);
        sy.max = clamp(sy.max, -20, 20);
        sz.min = clamp(sz.min, -20, 20);
        sz.max = clamp(sz.max, -20, 20);

        // convert f_dc_ to colors before calculating min/max and packaging
        for (let i = 0; i < f_dc_0.length; ++i) {
            f_dc_0[i] = dcDecode(f_dc_0[i]);
            f_dc_1[i] = dcDecode(f_dc_1[i]);
            f_dc_2[i] = dcDecode(f_dc_2[i]);
        }

        const cr = calcMinMax(f_dc_0);
        const cg = calcMinMax(f_dc_1);
        const cb = calcMinMax(f_dc_2);

        const packUnorm = (value: number, bits: number) => {
            const t = (1 << bits) - 1;
            return Math.max(0, Math.min(t, Math.floor(value * t + 0.5)));
        };

        const pack111011 = (x: number, y: number, z: number) => {
            return packUnorm(x, 11) << 21 |
                   packUnorm(y, 10) << 11 |
                   packUnorm(z, 11);
        };

        const pack8888 = (x: number, y: number, z: number, w: number) => {
            return packUnorm(x, 8) << 24 |
                   packUnorm(y, 8) << 16 |
                   packUnorm(z, 8) << 8 |
                   packUnorm(w, 8);
        };

        // pack quaternion into 2,10,10,10
        const packRot = (x: number, y: number, z: number, w: number) => {
            q.set(x, y, z, w).normalize();
            const a = [q.x, q.y, q.z, q.w];
            const largest = a.reduce((curr, v, i) => (Math.abs(v) > Math.abs(a[curr]) ? i : curr), 0);

            if (a[largest] < 0) {
                a[0] = -a[0];
                a[1] = -a[1];
                a[2] = -a[2];
                a[3] = -a[3];
            }

            const norm = Math.sqrt(2) * 0.5;
            let result = largest;
            for (let i = 0; i < 4; ++i) {
                if (i !== largest) {
                    result = (result << 10) | packUnorm(a[i] * norm + 0.5, 10);
                }
            }

            return result;
        };

        // pack
        for (let i = 0; i < this.size; ++i) {
            this.position[i] = pack111011(
                normalize(x[i], px.min, px.max),
                normalize(y[i], py.min, py.max),
                normalize(z[i], pz.min, pz.max)
            );

            this.rotation[i] = packRot(rot_0[i], rot_1[i], rot_2[i], rot_3[i]);

            this.scale[i] = pack111011(
                normalize(scale_0[i], sx.min, sx.max),
                normalize(scale_1[i], sy.min, sy.max),
                normalize(scale_2[i], sz.min, sz.max)
            );

            this.color[i] = pack8888(
                normalize(f_dc_0[i], cr.min, cr.max),
                normalize(f_dc_1[i], cg.min, cg.max),
                normalize(f_dc_2[i], cb.min, cb.max),
                1 / (1 + Math.exp(-opacity[i]))
            );
        }

        return { px, py, pz, sx, sy, sz, cr, cg, cb };
    }
}

// sort the compressed indices into morton order
const sortSplats = (splats: Splat[], indices: CompressedIndex[]) => {
    // https://fgiesen.wordpress.com/2009/12/13/decoding-morton-codes/
    const encodeMorton3 = (x: number, y: number, z: number) : number => {
        const Part1By2 = (x: number) => {
            x &= 0x000003ff;
            x = (x ^ (x << 16)) & 0xff0000ff;
            x = (x ^ (x <<  8)) & 0x0300f00f;
            x = (x ^ (x <<  4)) & 0x030c30c3;
            x = (x ^ (x <<  2)) & 0x09249249;
            return x;
        };

        return (Part1By2(z) << 2) + (Part1By2(y) << 1) + Part1By2(x);
    };

    let minx: number;
    let miny: number;
    let minz: number;
    let maxx: number;
    let maxy: number;
    let maxz: number;

    // calculate scene extents across all splats (using sort centers, because they're in world space)
    for (let i = 0; i < splats.length; ++i) {
        const splat = splats[i];
        const splatData = splat.splatData;
        const state = splatData.getProp('state') as Uint8Array;
        const { centers } = splat.entity.gsplat.instance.sorter;

        for (let i = 0; i < splatData.numSplats; ++i) {
            if ((state[i] & State.deleted) === 0) {
                const x = centers[i * 3 + 0];
                const y = centers[i * 3 + 1];
                const z = centers[i * 3 + 2];

                if (minx === undefined) {
                    minx = maxx = x;
                    miny = maxy = y;
                    minz = maxz = z;
                } else {
                    if (x < minx) minx = x; else if (x > maxx) maxx = x;
                    if (y < miny) miny = y; else if (y > maxy) maxy = y;
                    if (z < minz) minz = z; else if (z > maxz) maxz = z;
                }
            }
        }
    }

    const xlen = maxx - minx;
    const ylen = maxy - miny;
    const zlen = maxz - minz;

    const morton = new Uint32Array(indices.length);
    let idx = 0;
    for (let i = 0; i < splats.length; ++i) {
        const splat = splats[i];
        const splatData = splat.splatData;
        const state = splatData.getProp('state') as Uint8Array;
        const { centers } = splat.entity.gsplat.instance.sorter;

        for (let i = 0; i < splatData.numSplats; ++i) {
            if ((state[i] & State.deleted) === 0) {
                const x = centers[i * 3 + 0];
                const y = centers[i * 3 + 1];
                const z = centers[i * 3 + 2];

                const ix = Math.min(1023, Math.floor(1024 * (x - minx) / xlen));
                const iy = Math.min(1023, Math.floor(1024 * (y - miny) / ylen));
                const iz = Math.min(1023, Math.floor(1024 * (z - minz) / zlen));

                morton[idx++] = encodeMorton3(ix, iy, iz);
            }
        }
    }

    // order splats by morton code
    indices.sort((a, b) => morton[a.globalIndex] - morton[b.globalIndex]);
};

const serializePlyCompressed = async (splats: Splat[], options: SerializeSettings, fs: FileSystem, progress?: ProgressFunc): Promise<void> => {
    const { maxSHBands } = options;

    // create filter and count total gaussians
    const filter = new GaussianFilter(options);

    // make a list of indices spanning all splats (so we can sort them together)
    const indices: CompressedIndex[] = [];
    for (let splatIndex = 0; splatIndex < splats.length; ++splatIndex) {
        const splatData = splats[splatIndex].splatData;
        filter.set(splats[splatIndex]);
        for (let i = 0; i < splatData.numSplats; ++i) {
            if (filter.test(i)) {
                indices.push({ splatIndex, i, globalIndex: indices.length });
            }
        }
    }

    if (indices.length === 0) {
        console.error('nothing to export');
        return;
    }

    // create writer from filesystem
    const writer = await fs.createWriter('output.compressed.ply');

    const numSplats = indices.length;
    const numChunks = Math.ceil(numSplats / 256);

    const chunkProps = [
        'min_x', 'min_y', 'min_z',
        'max_x', 'max_y', 'max_z',
        'min_scale_x', 'min_scale_y', 'min_scale_z',
        'max_scale_x', 'max_scale_y', 'max_scale_z',
        'min_r', 'min_g', 'min_b',
        'max_r', 'max_g', 'max_b'
    ];

    const vertexProps = [
        'packed_position',
        'packed_rotation',
        'packed_scale',
        'packed_color'
    ];

    // calculate the number of output bands given the scene splat data and
    // user-chosen maxSHBands
    const outputSHBands = (() => {
        const splatBands = splats.map(s => calcSHBands(getVertexProperties(s.splatData)));
        return Math.min(maxSHBands ?? 3, Math.max(...splatBands));
    })();
    const outputSHCoeffs = shBandCoeffs[outputSHBands];

    const shHeader = outputSHBands ? [
        `element sh ${numSplats}`,
        new Array(outputSHCoeffs * 3).fill('').map((_, i) => `property uchar f_rest_${i}`)
    ].flat() : [];

    const headerText = [
        'ply',
        'format binary_little_endian 1.0',
        `comment ${generatedByString}`,
        `element chunk ${numChunks}`,
        chunkProps.map(p => `property float ${p}`),
        `element vertex ${numSplats}`,
        vertexProps.map(p => `property uint ${p}`),
        shHeader,
        'end_header\n'
    ].flat().join('\n');

    // sort splats into some kind of order (morton order rn)
    sortSplats(splats, indices);

    const singleSplat = new SingleSplat([
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3'
    ], options);

    const prepareChunk = (chunk: Chunk, i: number) => {
        const num = Math.min(numSplats, (i + 1) * 256) - i * 256;

        for (let j = 0; j < num; ++j) {
            const index = indices[i * 256 + j];

            // read splat
            singleSplat.read(splats[index.splatIndex], index.i);

            // update chunk
            chunk.set(j, singleSplat);
        }

        // pad the end of the last chunk with duplicate data
        if (num < 256) {
            for (let j = num; j < 256; ++j) {
                chunk.set(j, singleSplat);
            }
        }
    };

    const header = new TextEncoder().encode(headerText);

    const totalBytes =
        header.byteLength +
        numChunks * chunkProps.length * 4 +
        numSplats * vertexProps.length * 4 +
        outputSHCoeffs * 3 * numSplats;

    const progressWriter = new ProgressWriter(writer, totalBytes, progress);

    // write the header
    await progressWriter.write(header);

    const chunk = new Chunk();

    // write chunks
    const chunkData = new Float32Array(18);
    const chunkDataUint8 = new Uint8Array(chunkData.buffer);
    for (let i = 0; i < numChunks; ++i) {
        prepareChunk(chunk, i);

        const result = chunk.pack();

        chunkData[0] = result.px.min;
        chunkData[1] = result.py.min;
        chunkData[2] = result.pz.min;
        chunkData[3] = result.px.max;
        chunkData[4] = result.py.max;
        chunkData[5] = result.pz.max;

        chunkData[6] = result.sx.min;
        chunkData[7] = result.sy.min;
        chunkData[8] = result.sz.min;
        chunkData[9] = result.sx.max;
        chunkData[10] = result.sy.max;
        chunkData[11] = result.sz.max;

        chunkData[12] = result.cr.min;
        chunkData[13] = result.cg.min;
        chunkData[14] = result.cb.min;
        chunkData[15] = result.cr.max;
        chunkData[16] = result.cg.max;
        chunkData[17] = result.cb.max;

        await progressWriter.write(chunkDataUint8);
    }

    // write vertices
    const vertexData = new Uint32Array(256 * 4);
    const vertexDataUint8 = new Uint8Array(vertexData.buffer);
    for (let i = 0; i < numChunks; ++i) {
        const num = Math.min(numSplats, (i + 1) * 256) - i * 256;

        prepareChunk(chunk, i);
        chunk.pack();

        // write vertex data
        for (let j = 0; j < num; ++j) {
            vertexData[j * 4 + 0] = chunk.position[j];
            vertexData[j * 4 + 1] = chunk.rotation[j];
            vertexData[j * 4 + 2] = chunk.scale[j];
            vertexData[j * 4 + 3] = chunk.color[j];
        }

        await progressWriter.write(num === 256 ? vertexDataUint8 : new Uint8Array(vertexData.buffer, 0, num * 4 * 4));
    }

    // write sh
    const singleSplatSH = new SingleSplat(shNames.slice(0, outputSHCoeffs * 3), options);

    const shData = new Uint8Array(outputSHCoeffs * 3 * 256);
    for (let i = 0; i < numChunks; ++i) {
        const num = Math.min(numSplats, (i + 1) * 256) - i * 256;

        for (let j = 0; j < num; ++j) {
            const index = indices[i * 256 + j];

            // read splat
            singleSplatSH.read(splats[index.splatIndex], index.i);

            // quantize and write sh data
            const offset = j * outputSHCoeffs * 3;
            for (let k = 0; k < outputSHCoeffs * 3; ++k) {
                const nvalue = singleSplatSH.data[shNames[k]] / 8 + 0.5;
                shData[offset + k] = Math.max(0, Math.min(255, Math.trunc(nvalue * 256)));
            }
        }

        await progressWriter.write(num === 256 ? shData : new Uint8Array(shData.buffer, 0, num * outputSHCoeffs * 3));
    }

    progressWriter.close();
    await writer.close();
};

const serializeSplat = async (splats: Splat[], options: SerializeSettings, fs: FileSystem): Promise<void> => {
    // create writer from filesystem
    const writer = await fs.createWriter('output.splat');
    // create filter and count total gaussians
    const filter = new GaussianFilter(options);
    const totalGaussians = countGaussians(splats, filter);
    if (totalGaussians === 0) {
        return;
    }

    // position.xyz: float32, scale.xyz: float32, color.rgba: uint8, quaternion.ijkl: uint8
    const result = new Uint8Array(totalGaussians * 32);
    const dataView = new DataView(result.buffer);

    let idx = 0;

    const props = ['x', 'y', 'z', 'opacity', 'rot_0', 'rot_1', 'rot_2', 'rot_3', 'f_dc_0', 'f_dc_1', 'f_dc_2', 'scale_0', 'scale_1', 'scale_2'];
    const singleSplat = new SingleSplat(props, options);
    const { data } = singleSplat;

    const clamp = (x: number) => Math.max(0, Math.min(255, x));

    for (let e = 0; e < splats.length; ++e) {
        const splat = splats[e];
        const { splatData } = splat;
        filter.set(splat);

        for (let i = 0; i < splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            const off = idx++ * 32;

            dataView.setFloat32(off + 0, data.x, true);
            dataView.setFloat32(off + 4, data.y, true);
            dataView.setFloat32(off + 8, data.z, true);

            dataView.setFloat32(off + 12, Math.exp(data.scale_0), true);
            dataView.setFloat32(off + 16, Math.exp(data.scale_1), true);
            dataView.setFloat32(off + 20, Math.exp(data.scale_2), true);

            dataView.setUint8(off + 24, clamp(dcDecode(data.f_dc_0) * 255));
            dataView.setUint8(off + 25, clamp(dcDecode(data.f_dc_1) * 255));
            dataView.setUint8(off + 26, clamp(dcDecode(data.f_dc_2) * 255));
            dataView.setUint8(off + 27, clamp(sigmoid(data.opacity) * 255));

            dataView.setUint8(off + 28, clamp(data.rot_0 * 128 + 128));
            dataView.setUint8(off + 29, clamp(data.rot_1 * 128 + 128));
            dataView.setUint8(off + 30, clamp(data.rot_2 * 128 + 128));
            dataView.setUint8(off + 31, clamp(data.rot_3 * 128 + 128));
        }
    }

    await writer.write(result);
    await writer.close();
};

// Cached WebGPU device for SOG compression
let cachedGpuDevice: WebgpuGraphicsDevice | null = null;
let cachedBackbuffer: Texture | null = null;

// Configure splat-transform's worker/WASM loading for the bundled environment.
//
// The rollup bundle inlines splat-transform's JS into dist/index.js, but the
// worker script (dist/worker.mjs) and WASM binary (lib/webp.wasm) are separate
// assets that splat-transform auto-resolves via `import.meta.url`. rollup does
// NOT rewrite `new Worker(new URL('./worker.mjs', import.meta.url))` (that's a
// Vite/webpack feature), so we must point WorkerQueue.workerUrl at the deployed
// worker asset explicitly.
//
// History: previously this forced WorkerQueue.maxWorkers=0 (inline) because
// Electron loaded the renderer from file://, where module workers cannot spawn
// (CORS) and onerror never fires — stalling the WorkerQueue slot forever.
// Now Electron registers an app:// privileged protocol (see electron/main.cjs
// registerAppProtocol) which enables module workers, fetch(), and service
// workers. So we let maxWorkers auto-size (min(4, cores-1)) for ~3× parallel
// WebP encoding speedup. If a worker spawn fails at runtime, splat-transform
// falls back to inline automatically (per WorkerQueue contract).
let splatTransformConfigured = false;
const configureSplatTransform = () => {
    if (splatTransformConfigured) return;
    splatTransformConfigured = true;

    // Auto-size worker pool: min(4, cores-1). Workers spawn lazily and run
    // encodeWebp/quantize1d tasks off the main thread. Falls back to inline
    // if spawn fails (file:// without app:// protocol, or no Worker support).
    WorkerQueue.maxWorkers = null;

    // Resolve worker.mjs relative to this module (dist/index.js after bundling).
    // rollup.config.mjs copies dist/worker.mjs into dist/ alongside the bundle.
    try {
        WorkerQueue.workerUrl = new URL('./worker.mjs', import.meta.url).toString();
    } catch {
        // import.meta.url may be unavailable in some non-module contexts; leave
        // the default and let splat-transform's own resolution attempt run.
    }

    // Resolve webp.wasm relative to this module (dist/index.js after bundling).
    // rollup.config.mjs copies the wasm into dist/ alongside the bundle.
    try {
        WebPCodec.wasmUrl = new URL('./webp.wasm', import.meta.url).toString();
    } catch {
        // import.meta.url may be unavailable in some non-module contexts; leave
        // the default and let splat-transform's own resolution attempt run.
    }
};

const createGpuDevice = async (): Promise<WebgpuGraphicsDevice> => {
    configureSplatTransform();

    if (cachedGpuDevice) {
        return cachedGpuDevice;
    }

    if (!navigator.gpu) {
        throw new Error('WebGPU is not available in this browser');
    }

    // Create a minimal canvas for the graphics device
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;

    const graphicsDevice = new WebgpuGraphicsDevice(canvas, {
        antialias: false,
        depth: false,
        stencil: false
    });

    await graphicsDevice.createDevice();

    // Create external backbuffer (required by PlayCanvas)
    cachedBackbuffer = new Texture(graphicsDevice, {
        width: 1024,
        height: 512,
        name: 'SogComputeBackbuffer',
        mipmaps: false,
        format: PIXELFORMAT_BGRA8
    });

    // @ts-ignore - externalBackbuffer is an internal property
    graphicsDevice.externalBackbuffer = cachedBackbuffer;

    cachedGpuDevice = graphicsDevice;
    return graphicsDevice;
};

/**
 * Extract Splat data into a DataTable for use with splat-transform writers.
 * This is shared between serializeSog and serializeViewer.
 */
const extractDataTable = (splats: Splat[], settings: SerializeSettings): DataTable => {
    const { maxSHBands = 3 } = settings;

    // Determine which members to extract
    const shCoeffs = [0, 3, 8, 15][maxSHBands];
    const memberNames = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3',
        ...shNames.slice(0, shCoeffs * 3)
    ];

    // Create SingleSplat for data extraction
    const singleSplat = new SingleSplat(memberNames, settings);

    // Create filter
    const filter = new GaussianFilter(settings);

    // Count total gaussians to export
    let totalCount = 0;
    for (const splat of splats) {
        filter.set(splat);
        for (let i = 0; i < splat.splatData.numSplats; ++i) {
            if (filter.test(i)) {
                totalCount++;
            }
        }
    }

    if (totalCount === 0) {
        throw new Error('No gaussians to export');
    }

    // Create DataTable columns. Tag the table with Transform.PLY because
    // SingleSplat.read pre-applies the PLY-style 180° Z flip (see
    // SplatTransformCache.getMat), so the column data is in PLY space.
    // Without this, splat-transform writers (writeSog, writeHtml) would apply
    // the flip a second time and produce upside-down output.
    const columns = memberNames.map(name => new Column(name, new Float32Array(totalCount)));
    const dataTable = new DataTable(columns, Transform.PLY);

    // Extract data into DataTable
    let idx = 0;
    for (const splat of splats) {
        filter.set(splat);
        for (let i = 0; i < splat.splatData.numSplats; ++i) {
            if (!filter.test(i)) continue;

            singleSplat.read(splat, i);

            for (let j = 0; j < memberNames.length; ++j) {
                (columns[j].data as Float32Array)[idx] = singleSplat.data[memberNames[j]] ?? 0;
            }
            idx++;
        }
    }

    return dataTable;
};

// Bridge splat-transform progress events to ReSplat's events.
const createProgressRenderer = (header: string, events?: Events): Renderer => ({
    handle: (event: LogEvent) => {
        switch (event.kind) {
            case 'scopeStart':
                if (event.depth === 0) {
                    events?.fire('progressStart', header);
                } else {
                    events?.fire('progressUpdate', {
                        text: event.index !== undefined && event.total !== undefined ?
                            `Step ${event.index} of ${event.total}: ${event.name}` :
                            event.name,
                        progress: 0
                    });
                }
                break;
            case 'scopeEnd':
                if (event.depth === 0) {
                    events?.fire('progressEnd');
                }
                break;
            case 'barStart':
                events?.fire('progressUpdate', { text: event.name, progress: 0 });
                break;
            case 'barTick':
                events?.fire('progressUpdate', {
                    progress: event.total > 0 ? 100 * event.current / event.total : 0
                });
                break;
            case 'barEnd':
                events?.fire('progressUpdate', { progress: 100 });
                break;
            case 'message':
                if (event.level === 'error') console.error(event.text);
                else if (event.level === 'warn') console.warn(event.text);
                else if (event.level === 'info') console.info(event.text);
                else if (event.level === 'debug') console.debug(event.text);
                break;
            case 'output':
                console.log(event.text);
                break;
        }
    }
});

const serializeViewer = async (splats: Splat[], serializeSettings: SerializeSettings, options: ViewerExportSettings, fs: FileSystem): Promise<void> => {
    const { experienceSettings, events } = options;

    configureSplatTransform();
    splatTransformLogger.setRenderer(createProgressRenderer('Exporting HTML', events));

    // Extract splat data to DataTable
    const dataTable = extractDataTable(splats, serializeSettings);

    // splat-transform's writers leave their top-level scope open on error
    // (their contract is for the caller to unwind), so we explicitly
    // unwind here to deliver a matching depth-0 `scopeEnd(failed)` to the
    // renderer. That fires `progressEnd` and dismisses the dialog before
    // any error popup is shown.
    try {
        if (options.type === 'html') {
            // Bundled HTML - writeHtml handles everything
            await writeHtml({
                filename: 'output.html',
                dataTable,
                viewerSettingsJson: experienceSettings,
                bundle: true,
                iterations: 0,
                createDevice: createGpuDevice
            }, fs);
        } else {
            // Package - use unbundled mode into a MemoryFileSystem, then ZIP
            const memFs = new MemoryFileSystem();
            await writeHtml({
                filename: 'index.html',
                dataTable,
                viewerSettingsJson: experienceSettings,
                bundle: false,
                iterations: 0,
                createDevice: createGpuDevice
            }, memFs);

            // Create ZIP from memory filesystem results. The try/finally
            // ensures zipFs (and its underlying writer) is closed even if a
            // write throws partway through, so we don't leak the output file.
            const zipWriter = await fs.createWriter('output.zip');
            const zipFs = new ZipFileSystem(zipWriter);
            try {
                for (const [filename, data] of memFs.results.entries()) {
                    const writer = await zipFs.createWriter(filename);
                    await writer.write(data);
                    await writer.close();
                }
            } finally {
                await zipFs.close();
            }
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

// SOG serialization using splat-transform library

type SogSettings = SerializeSettings & {
    iterations: number;
    events?: Events;
    filename?: string;
};

// LCC2 (Lixel CyberColor 2) directory-format export options. Phase 1 exercises
// a single LOD with splatType='.sog'; lodLevels>1 and splatType='.spz' are
// reserved for Phase 2 (LOD auto-generation) and treated as 1/'.sog' here.
type Lcc2ExportOptions = {
    name: string;                          // scene/file base name (no extension)
    splatIdx?: number[] | null;            // selected splat indices into the splats array
    serializeSettings: SogSettings;        // reuse serializeSog's settings (maxSHBands, iterations, events, ...)
    lodLevels?: number;                    // default 1 (Phase 1; >1 reserved for Phase 2, treated as 1)
    splatType?: '.sog' | '.spz';           // default '.sog' (Phase 1 only '.sog' is exercised)
};

const serializeSog = async (splats: Splat[], settings: SogSettings, fs: FileSystem): Promise<void> => {
    const { iterations = 0, events, filename = 'output.sog' } = settings;

    configureSplatTransform();
    splatTransformLogger.setRenderer(createProgressRenderer('Exporting SOG', events));

    // Extract splat data to DataTable
    const dataTable = extractDataTable(splats, settings);

    // splat-transform's writers leave their top-level scope open on error
    // (their contract is for the caller to unwind), so we explicitly
    // unwind here to deliver a matching depth-0 `scopeEnd(failed)` to the
    // renderer. That fires `progressEnd` and dismisses the dialog before
    // any error popup is shown.
    try {
        await writeSogInternal({
            filename,
            dataTable,
            bundle: true,
            iterations,
            createDevice: createGpuDevice
        }, fs);
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

// Apply the PLY→LCC2 coordinate conversion in-place on a PLY-space DataTable.
//
// writeSog always encodes in PLY space (bakeTransform → Z(180) rotation).
// readLcc2Source applies LCC2_TRANSFORM = fromEulers(90, 0, 180) on top of
// the PLY-space SOG data. Without compensation the import round-trip would
// accumulate an extraneous Rx(-90°) rotation. The spatial chain
//   p_lcc2 = Rz(180°) * Rx(90°) * Rz(180°) * p_ply
// simplifies to Rx(-90°) = (x, z, -y). Positions, rotation quaternions, and
// degree-1 SH coefficients all receive the same Rx(-90°) transform so each
// gaussian's orientation, shape, and first-order view-dependent lighting
// stay consistent.
//
//   Position:     (x, y, z) → (x, z, -y).
//   Quaternion:   q' = Rx(-90°) * q  (left-multiply, w-first order).
//   SH l=1:       (y→z, z→-y, x unchanged) per channel.
//
// Higher-degree SH (l≥2) are not compensated; their angular power is small
// enough that the visual impact is negligible for export. Returns the scene
// AABB in LCC2 space (splat-center extents, not scale-expanded).
const applyLcc2CoordinateTransform = (dataTable: DataTable, maxSHBands: number): { min: [number, number, number]; max: [number, number, number] } => {
    const N = dataTable.numRows;
    const xs = dataTable.getColumnByName('x')!.data as Float32Array;
    const ys = dataTable.getColumnByName('y')!.data as Float32Array;
    const zs = dataTable.getColumnByName('z')!.data as Float32Array;
    const r0 = dataTable.getColumnByName('rot_0')!.data as Float32Array;
    const r1 = dataTable.getColumnByName('rot_1')!.data as Float32Array;
    const r2 = dataTable.getColumnByName('rot_2')!.data as Float32Array;
    const r3 = dataTable.getColumnByName('rot_3')!.data as Float32Array;
    const SQRT1_2 = Math.SQRT1_2;

    // Degree-1 SH rotation. getColumnByName returns Column|null, so test for
    // null (not undefined) — an earlier version used `!== undefined` which is
    // always true for a Column|null return and would crash on SH-less scenes.
    const shCoeffs = [0, 3, 8, 15][maxSHBands];
    let shCols: Float32Array[] | null = null;
    if (shCoeffs >= 3 && dataTable.getColumnByName('f_rest_0')) {
        shCols = [];
        for (let ch = 0; ch < 3; ++ch) {
            const base = ch * shCoeffs;
            shCols.push(
                dataTable.getColumnByName(`f_rest_${base + 0}`)!.data as Float32Array,
                dataTable.getColumnByName(`f_rest_${base + 1}`)!.data as Float32Array
            );
        }
    }

    for (let i = 0; i < N; ++i) {
        const x = xs[i];
        const y = ys[i];
        const z = zs[i];
        xs[i] = x;
        ys[i] = z;
        zs[i] = -y;

        // q' = Rx(-90°) * q  (left-multiply, w-first order)
        const qw = r0[i], qx = r1[i], qy = r2[i], qz = r3[i];
        r0[i] = (qw + qx) * SQRT1_2;
        r1[i] = (qx - qw) * SQRT1_2;
        r2[i] = (qy + qz) * SQRT1_2;
        r3[i] = (qz - qy) * SQRT1_2;

        // SH l=1: rotate per-channel (y→z, z→-y, x unchanged)
        if (shCols) {
            for (let ch = 0; ch < 3; ++ch) {
                const s0 = shCols[ch * 2 + 0], s1 = shCols[ch * 2 + 1];
                const v0 = s0[i], v1 = s1[i];
                s0[i] = v1;
                s1[i] = -v0;
            }
        }
    }

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < N; ++i) {
        const x = xs[i], y = ys[i], z = zs[i];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (z < minZ) minZ = z;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
        if (z > maxZ) maxZ = z;
    }
    return { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
};

// LCC2 node metadata shape (matches the v0.0.3 schema).
type Lcc2NodeRef = { name: number; start: number; count: number };
type Lcc2Aabb = { min: [number, number, number]; max: [number, number, number] };

// Adaptive hybrid tree node. Built once on the finest splats and walked
// per-depth to emit LOD-sampled chunks. Mirrors the reference LCC2 topology
// (英华楼.lcc2: root has 21 spatial-cell children, ~234 LOD-chain nodes,
// ~95 spatial-split nodes, node count grows linearly 21→21→24→33→49→67→113→182
// across 8 depths — NOT exponentially).
//
//   child === null       → leaf (depth === treeDepth, finest LOD level)
//   child.length === 1   → LOD chain (same AABB as parent; next-finer LOD
//                          level in the SAME spatial cell). Dominant case.
//   child.length > 1     → spatial split (each child has a smaller, tight
//                          AABB; happens only when a cell's finest count
//                          exceeds sogChunkTarget so it must subdivide for
//                          streaming granularity).
//
// `finestIndices` holds ALL finest-level splat indices belonging to this
// spatial cell (NOT LOD-sampled). LOD chains share the parent's array by
// reference (zero copy); spatial splits allocate subset arrays (sum of
// children lengths = parent length). Per-depth LOD subsets are extracted on
// demand during chunk writing via interval sampling at step 2^(treeDepth-D).
type AdaptiveNode = {
    aabb: Lcc2Aabb;
    finestIndices: Uint32Array;
    child: AdaptiveNode[] | null;
    id?: string;
};

// Compute a tight AABB around the given splat indices. Used at split time so
// each child cell gets a minimal (data-fitted) bounding box rather than the
// theoretical octant box — matches the reference structure where parent and
// LOD-chain child share an identical tight AABB.
const computeTightAabb = (
    indices: Uint32Array,
    xs: Float32Array, ys: Float32Array, zs: Float32Array
): Lcc2Aabb => {
    let mn0 = Infinity, mn1 = Infinity, mn2 = Infinity;
    let mx0 = -Infinity, mx1 = -Infinity, mx2 = -Infinity;
    for (let i = 0; i < indices.length; ++i) {
        const idx = indices[i];
        const x = xs[idx], y = ys[idx], z = zs[idx];
        if (x < mn0) mn0 = x;
        if (y < mn1) mn1 = y;
        if (z < mn2) mn2 = z;
        if (x > mx0) mx0 = x;
        if (y > mx1) mx1 = y;
        if (z > mx2) mx2 = z;
    }
    return { min: [mn0, mn1, mn2], max: [mx0, mx1, mx2] };
};

// Build an adaptive hybrid tree on the finest splats.
//
// At each depth D (1..treeDepth) a node either:
//   - chains: single child with the SAME aabb (LOD link to the next-finer
//     level in the same spatial cell), OR
//   - splits: octree 8-way subdivision (empty octants dropped), each child
//     getting a tight data-fitted AABB.
//
// Split decision: subdivide when the cell's finest splat count exceeds
// `sogChunkTarget`. This keeps per-cell finest data streamable in one chunk
// and produces linear node growth (~cells × treeDepth) instead of the
// exponential 2^(L+2) growth of the old fixed binary tree — which is what
// froze the UE plugin for lodLevels > 6.
//
// The octree branch factor (max 8 children per split, empty octants dropped)
// matches the reference's variable childNum distribution (2..5 observed; 8
// max). Root childNum is data-dependent (8..64 typically), giving the UE
// plugin enough spatial cells for streaming without an explosion of nodes.
//
// Memory: LOD chains share `finestIndices` by reference (zero copy). Only
// spatial splits allocate new Uint32Arrays, and their lengths sum to the
// parent's. Total allocation ≈ N × (number of split depths), typically 1-3
// levels → 1N-3N, far below the 4GB browser limit.
//
// `maxSplitDepth` caps recursion for degenerate inputs (e.g. all splats at
// one point) where octree subdivision never reduces cell count below target.
const buildAdaptiveLcc2Tree = (
    xs: Float32Array, ys: Float32Array, zs: Float32Array,
    N: number, treeDepth: number, sceneAabb: Lcc2Aabb,
    splitTarget: number,
    maxSplitDepth = 8
): AdaptiveNode => {
    const rootIndices = new Uint32Array(N);
    for (let i = 0; i < N; ++i) rootIndices[i] = i;

    // Recursively build children for a cell holding `indices` at `depth`.
    // Returns null past leaf depth, otherwise 1 (chain) or 2..8 (split) children.
    // depth ranges 1..treeDepth inclusive: depth=treeDepth is the finest level
    // (k=0), so we must NOT stop at depth===treeDepth — stopping there would
    // drop the finest LOD and leave the UE plugin with no k=0 data (freeze).
    const buildChildren = (
        indices: Uint32Array, aabb: Lcc2Aabb, depth: number, splitDepth: number
    ): AdaptiveNode[] | null => {
        if (depth > treeDepth) return null;

        // LOD chain when the cell is small enough OR we've exhausted split budget.
        if (indices.length <= splitTarget || splitDepth >= maxSplitDepth) {
            const child: AdaptiveNode = { aabb, finestIndices: indices, child: null };
            child.child = buildChildren(indices, aabb, depth + 1, splitDepth);
            return [child];
        }

        // Spatial split: octree 8-way at the AABB midpoints, drop empty octants.
        const [mn0, mn1, mn2] = aabb.min;
        const [mx0, mx1, mx2] = aabb.max;
        const mid0 = (mn0 + mx0) * 0.5;
        const mid1 = (mn1 + mx1) * 0.5;
        const mid2 = (mn2 + mx2) * 0.5;

        const bucketCounts = new Uint32Array(8);
        for (let i = 0; i < indices.length; ++i) {
            const idx = indices[i];
            const bX = xs[idx] <= mid0 ? 0 : 1;
            const bY = ys[idx] <= mid1 ? 0 : 1;
            const bZ = zs[idx] <= mid2 ? 0 : 1;
            bucketCounts[(bX << 2) | (bY << 1) | bZ]++;
        }

        // Pre-allocate per-octant index arrays.
        const bucketArrays: (Uint32Array | null)[] = new Array(8).fill(null);
        const bucketFill = new Uint32Array(8);
        for (let o = 0; o < 8; ++o) {
            if (bucketCounts[o] > 0) bucketArrays[o] = new Uint32Array(bucketCounts[o]);
        }
        for (let i = 0; i < indices.length; ++i) {
            const idx = indices[i];
            const bX = xs[idx] <= mid0 ? 0 : 1;
            const bY = ys[idx] <= mid1 ? 0 : 1;
            const bZ = zs[idx] <= mid2 ? 0 : 1;
            const o = (bX << 2) | (bY << 1) | bZ;
            bucketArrays[o]![bucketFill[o]++] = idx;
        }

        const children: AdaptiveNode[] = [];
        for (let o = 0; o < 8; ++o) {
            if (bucketCounts[o] === 0) continue; // drop empty octant
            const childIndices = bucketArrays[o]!;
            // Tighten AABB to actual splat bounds (smaller than the octant box).
            const childAabb = computeTightAabb(childIndices, xs, ys, zs);
            const child: AdaptiveNode = { aabb: childAabb, finestIndices: childIndices, child: null };
            child.child = buildChildren(childIndices, childAabb, depth + 1, splitDepth + 1);
            children.push(child);
        }
        // Guard: if all splats landed in one octant (degenerate data), fall back
        // to a chain to avoid infinite recursion.
        if (children.length === 1) {
            // Already built — but force chain semantics by stopping further splits.
            const only = children[0];
            only.child = buildChildren(only.finestIndices, only.aabb, depth + 1, maxSplitDepth);
        }
        return children;
    };

    const root: AdaptiveNode = { aabb: sceneAabb, finestIndices: rootIndices, child: null };
    root.child = buildChildren(rootIndices, sceneAabb, 1, 0);
    return root;
};

// Assign node IDs in the XGRIDS path convention: "0", "0_0", "0_0_1", ...
// Called once after tree construction, before emitAdaptiveTreeJson or any
// file naming that depends on node IDs. The child array index determines the
// serial number in the ID.
const assignAdaptiveNodeIds = (root: AdaptiveNode): void => {
    const walk = (node: AdaptiveNode, id: string) => {
        node.id = id;
        if (node.child) {
            for (let i = 0; i < node.child.length; ++i) {
                walk(node.child[i], `${id}_${i}`);
            }
        }
    };
    walk(root, '0');
};

// Collect all tree nodes at exactly `targetDepth` (root is depth 0; root's
// children are depth 1). Used by the per-depth chunk writer to iterate every
// spatial cell that contributes data to LOD level (treeDepth - targetDepth).
const collectNodesAtDepth = (root: AdaptiveNode, targetDepth: number): AdaptiveNode[] => {
    const result: AdaptiveNode[] = [];
    const walk = (node: AdaptiveNode, depth: number) => {
        if (depth === targetDepth) {
            result.push(node);
            return;
        }
        if (node.child) {
            for (const c of node.child) walk(c, depth + 1);
        }
    };
    if (root.child) {
        for (const c of root.child) walk(c, 1);
    }
    return result;
};

// Descend the adaptive tree to find the depth-`targetDepth` node whose AABB
// contains the point (x,y,z). Used by serializeLcc2FromLodLog to bin coarser-
// LOD splats (loaded from file) into the same tree that was built on the
// finest LOD.
//
// Descent rules:
//   - At the root, find the depth-1 child whose AABB contains the point.
//   - At a LOD-chain node (childNum===1), follow the single child (same AABB).
//   - At a spatial-split node (childNum>1), find the child whose AABB contains
//     the point.
//   - If no child contains the point (degenerate — point on a boundary, or
//     floating-point drift), return the current node so the splat is not lost.
//
// O(targetDepth × max_children) per call. For targetDepth ≤ 20 and max 8
// children: ≤160 comparisons per splat. Coarser LODs are small (N/2^k), so
// total binning work across all coarser levels ≈ 6N comparisons (geometric
// series) — a few seconds for N = 35M.
const findAdaptiveNodeAtDepth = (
    root: AdaptiveNode,
    x: number, y: number, z: number,
    targetDepth: number
): AdaptiveNode | null => {
    if (!root.child || root.child.length === 0) return null;

    // Find the depth-1 child containing the point.
    let node: AdaptiveNode | null = null;
    for (const c of root.child) {
        const [mn0, mn1, mn2] = c.aabb.min;
        const [mx0, mx1, mx2] = c.aabb.max;
        if (x >= mn0 && x <= mx0 && y >= mn1 && y <= mx1 && z >= mn2 && z <= mx2) {
            node = c;
            break;
        }
    }
    if (!node) return null;

    for (let d = 2; d <= targetDepth; ++d) {
        if (!node.child || node.child.length === 0) return node; // leaf
        if (node.child.length === 1) {
            // LOD chain: child shares the same AABB, just follow.
            node = node.child[0];
            continue;
        }
        // Spatial split: find the child whose AABB contains the point.
        let next: AdaptiveNode | null = null;
        for (const c of node.child) {
            const [mn0, mn1, mn2] = c.aabb.min;
            const [mx0, mx1, mx2] = c.aabb.max;
            if (x >= mn0 && x <= mx0 && y >= mn1 && y <= mx1 && z >= mn2 && z <= mx2) {
                next = c;
                break;
            }
        }
        if (!next) return node; // degenerate: point on boundary or FP drift
        node = next;
    }
    return node;
};

// Emit the nested LCC2 tree JSON from the adaptive tree. Node IDs follow the
// path convention `0`, `0_0`, `0_0_0`, ... matching the reference structure.
// `nodeRefs` maps each AdaptiveNode to its {name, start, count} chunk ref
// (assigned during per-depth chunk writing). Nodes with no ref or count===0
// get data:null — but with the adaptive tree every node has finest splats, so
// every depth has data. Returns the root JSON object (without splatFiles/
// meshFiles/bvhFiles, which the caller attaches).
const emitAdaptiveTreeJson = (
    root: AdaptiveNode,
    nodeRefs: Map<AdaptiveNode, Lcc2NodeRef>,
    splatFiles: string[]
): any => {
    const emit = (node: AdaptiveNode): any => {
        const ref = nodeRefs.get(node);
        let data = ref && ref.count > 0 ? { '3dgs': ref } : null;
        if (!node.child || node.child.length === 0) {
            return { id: node.id, boundingBox: node.aabb, childNum: 0, data };
        }
        const child: Record<string, any> = {};
        for (let i = 0; i < node.child.length; ++i) {
            child[String(i)] = emit(node.child[i]);
        }
        // Chain nodes (childNum=1, same AABB) with 0 own splats at their LOD
        // level get a minimal 1-splat data reference from the first child.
        // Degenerate cells (zero-volume AABB from coincident splats) can yield
        // count=0 at coarser LODs; the UE plugin's LCC2Tree::ComputeError
        // dereferences node.data.3dgs unconditionally, so null data →
        // EXCEPTION_ACCESS_VIOLATION. We assign count=1 (not full inheritance)
        // because full inheritance would make the UE plugin render the finer
        // LOD's entire splat count for this cell — up to 2.8x more splats per
        // frame (~20fps drop). 1 representative splat is enough for ComputeError
        // and keeps render cost minimal; the plugin descends to finer LODs
        // (showing full detail) when the camera is close. Children are emitted
        // above, so child['0'].data is already resolved (cascades through
        // multi-level chains).
        if (!data && child['0'] && child['0'].data && child['0'].data['3dgs']) {
            const cd = child['0'].data['3dgs'] as { name: number; start: number; count: number };
            data = { '3dgs': { name: cd.name, start: cd.start, count: 1 } };
        }
        return { id: node.id, boundingBox: node.aabb, childNum: node.child.length, data, child };
    };

    const child: Record<string, any> = {};
    if (root.child) {
        for (let i = 0; i < root.child.length; ++i) {
            child[String(i)] = emit(root.child[i]);
        }
    }
    return {
        id: root.id,
        boundingBox: root.aabb,
        childNum: root.child?.length ?? 0,
        data: null,
        splatFiles,
        meshFiles: [] as string[],
        bvhFiles: [] as string[],
        child
    };
};

// LCC2 (Lixel CyberColor 2) directory-format serialization. Phase 1 writes a
// single LOD; Phase 2 (lodLevels>1) builds a multi-level LOD tree.
//
// Phase 2 LOD generation (adaptive hybrid tree):
//   - An adaptive tree of depth L is built on the finest splats. Each node
//     either chains (childNum=1, same AABB → LOD link to next-finer level in
//     the same spatial cell) or splits (octree 8-way, empty octants dropped →
//     spatial subdivision for streaming granularity). See buildAdaptiveLcc2Tree.
//   - LOD level k (0=finest..L-1=coarsest) samples each cell's finestIndices
//     at rate = 1 - 0.9*k/(L-1): LOD0=100%, LOD(L-1)=10%, linear in between.
//     This guarantees strictly decreasing fidelity per level (no duplicates),
//     with the coarsest level always holding 10% — unlike the old step=2^k
//     scheme which collapsed to 100/50/25/12.5… and starved coarse LODs.
//   - All depth-D nodes' samples are concatenated into one or more .sog chunks
//     (≤ SOG_CHUNK_TARGET splats each); each node's {name, start, count} points
//     into its level's chunk(s).
//   - The finest level (k=0, rate=100%) is written directly from the finest
//     DataTable with `indices` = the node-ordered full permutation (no clone;
//     writeSog skips its internal Morton sort and emits in our order).
//   - Coarser levels (k≥1) are written from a column-by-column subset (sized
//     to N×rate, never full-size) with identity `indices` to preserve node
//     order. Peak memory = finest + one smaller subset.
//   - splatFiles is ordered coarsest→finest (depth 1 → index 0), matching the
//     reference sample; node.data.3dgs.name = the splatFiles index of the
//     chunk holding that node's LOD-D data.
//   - Node count grows LINEARLY with L (≈ cells × L), so the UE plugin can
//     traverse high-LOD trees without freezing — unlike the old fixed binary
//     tree whose 2^(L+2) leaves froze the plugin for L > 6.
const serializeLcc2 = async (splats: Splat[], options: Lcc2ExportOptions, fs: FileSystem): Promise<void> => {
    const { name, serializeSettings, splatIdx } = options;
    const { iterations = 0, events } = serializeSettings;
    const splatType = options.splatType ?? '.sog';
    // Clamp lodLevels to [1,20]. Max 20 matches the UE plugin's EndLevel cap
    // (LCCMacro.h). The adaptive hybrid tree keeps node count linear in L, so
    // values up to 20 are safe — unlike the old binary tree which froze the
    // plugin past 6.
    let lodLevels = Math.max(1, Math.min(20, options.lodLevels ?? 6));

    const selectedSplats = splatIdx?.length ? splatIdx.map(i => splats[i]) : splats;

    await fs.mkdir(`${name}/data/3dgs`);

    configureSplatTransform();

    // Custom progress renderer: suppresses progressStart/progressEnd (dialog
    // lifecycle managed manually) and shows "正在导出 LOD{N}" instead of raw
    // splat-transform step names like "k-means". Sub-step bar progress is
    // suppressed to prevent resetting the cumulative percentage.
    let currentExportLod = -1;
    let lodExportProgress = 0;
    splatTransformLogger.setRenderer({
        handle: (event: LogEvent) => {
            switch (event.kind) {
                case 'scopeStart':
                    if (event.depth > 0) {
                        const label = currentExportLod >= 0 ? `正在导出 LOD${currentExportLod}` : event.name;
                        events?.fire('progressUpdate', { text: `${label}  —  ${lodExportProgress}%` });
                    }
                    break;
                case 'scopeEnd':
                    break;
                case 'barStart':
                case 'barTick':
                case 'barEnd':
                    break;
                case 'message':
                    if (event.level === 'error') console.error(event.text);
                    else if (event.level === 'warn') console.warn(event.text);
                    break;
            }
        }
    });

    events?.fire('progressStart', 'Exporting LCC2');

    // Cap maxSHBands to the source splats' actual SH bands. extractDataTable
    // allocates Float32Array(totalCount) per output column; if the user requests
    // SH=3 (59 cols) but the source is SH=0 (14 cols), the 45 f_rest columns
    // are zero-filled — wasting N×45×4 bytes (e.g. 35.7M splats → 3.2 GB) and
    // pushing peak memory past the ~4 GB renderer limit ("Array buffer
    // allocation failed"). Mirrors serializePlyCompressed's outputSHBands logic.
    const requestedSHBands = serializeSettings.maxSHBands ?? 3;
    const sourceMaxSHBands = Math.max(...selectedSplats.map(s => calcSHBands(getVertexProperties(s.splatData))));
    const maxSHBands = Math.min(requestedSHBands, sourceMaxSHBands);
    if (maxSHBands < requestedSHBands) {
        console.warn(
            `[LCC2] Source SH=${sourceMaxSHBands} < requested SH=${requestedSHBands}; ` +
            `capping to SH=${maxSHBands} (avoids zero-fill column OOM)`
        );
    }
    const exportSettings: SerializeSettings = { ...serializeSettings, maxSHBands };

    // Build the splat-transform DataTable ONCE (PLY space). SingleSplat.read
    // pre-applies the PLY-style 180° Z flip + world transform.
    const dataTable = extractDataTable(selectedSplats, exportSettings);
    const N = dataTable.numRows;

    // Convert PLY-space data to LCC2-native space (in-place). Done before any
    // clone so coarser-level subsets inherit the transform.
    const boundingBox = applyLcc2CoordinateTransform(dataTable, maxSHBands);

    const fileType = maxSHBands > 0 ? 'quality' : 'portable';
    const guid = crypto.randomUUID().replace(/-/g, '');

    // splatFiles[k-file-index] : depth D -> index D-1 (coarsest first).
    // chunkPaths[k] : level k (0=finest). splatFiles[d] = chunkPaths[L-1-d].
    const splatFiles: string[] = [];
    const lodSplats: number[] = [];
    let root: any;

    try {
        // Auto-select tree depth (LOD levels).
        //   - safeTreeDepth: minimum for chunk safety (per-depth total ≤ SOG_CHUNK_TARGET)
        //   - User slider value is honored; only raised when needed for chunk safety.
        //   - Per-depth merging means small nodes don't cause file explosion.
        //   - Max 20 = UE plugin's EndLevel cap (LCCMacro.h). The adaptive tree
        //     keeps node count linear in L, so values up to 20 are safe.
        const SOG_CHUNK_TARGET = 3_000_000;
        // Per-cell spatial split threshold (~500K splats). Cells below this
        // size chain (LOD link) instead of splitting. This is deliberately
        // much smaller than SOG_CHUNK_TARGET because the chunking loop
        // already packs multiple spatial cells into one .sog file (up to
        // SOG_CHUNK_TARGET). A smaller split threshold creates more spatial
        // cells → finer LOD granularity → the UE plugin renders fewer splats
        // per visible node (fewer draw calls per cell). The source XGRIDS
        // format uses ~400K max per cell; targeting ~500K matches that.
        const SPLIT_TARGET = 500_000;
        const safeTreeDepth = Math.max(1, Math.ceil(Math.log2(N / SOG_CHUNK_TARGET)) - 2);
        const treeDepth = Math.min(20, Math.max(safeTreeDepth, lodLevels));
        if (treeDepth > lodLevels) {
            console.warn(
                `[LCC2] auto-raised lodLevels ${lodLevels}→${treeDepth} ` +
                `(N=${N.toLocaleString()}, SOG_CHUNK_TARGET=${SOG_CHUNK_TARGET.toLocaleString()})`
            );
        }
        // Rough encode-time estimate.
        const shFactor = maxSHBands > 0 ? 1 : 0.25;
        const estMin = Math.max(1, Math.round((N / 3.8e6) * 10 * shFactor));
        console.warn(`[LCC2] N=${N.toLocaleString()} treeDepth=${treeDepth} SH=${maxSHBands} est ~${estMin} min`);
        lodLevels = treeDepth;

        if (treeDepth <= 1) {
            // ---- Phase 1: single LOD ----
            const lodFileName = '0_0.sog';
            const chunkPath = `${name}/data/3dgs/${lodFileName}`;

            currentExportLod = 0;
            lodExportProgress = 100;
            events?.fire('progressUpdate', { text: '正在导出 LOD0  —  0%', progress: 0 });

            await writeSogInternal({
                filename: chunkPath,
                dataTable,
                bundle: true,
                iterations,
                createDevice: createGpuDevice
            }, fs);

            events?.fire('progressUpdate', { text: `LOD 0: ${N.toLocaleString()} splats  —  100%`, progress: 100 });
            splatFiles.push(`data/3dgs/${lodFileName}`);
            lodSplats.push(N);
            root = {
                id: '0',
                boundingBox,
                childNum: 1,
                data: null,
                splatFiles,
                meshFiles: [] as string[],
                bvhFiles: [] as string[],
                child: {
                    '0': {
                        id: '0_0',
                        boundingBox,
                        childNum: 0,
                        data: { '3dgs': { name: 0, start: 0, count: N } }
                    }
                }
            };
        } else {
            // ---- Phase 2: multi-level LOD with adaptive hybrid tree ----
            //
            // The adaptive tree is built once on the finest splats
            // (buildAdaptiveLcc2Tree). Each depth D (1..treeDepth) holds one
            // LOD level; nodes at depth D are collected and their finestIndices
            // are interval-sampled at step 2^(treeDepth-D) to produce that
            // level's data. All depth-D samples are concatenated into 1..N
            // .sog chunks (≤ SOG_CHUNK_TARGET splats each); each node's
            // {name, start, count} points into its level's chunk(s).
            const xs = dataTable.getColumnByName('x')!.data as Float32Array;
            const ys = dataTable.getColumnByName('y')!.data as Float32Array;
            const zs = dataTable.getColumnByName('z')!.data as Float32Array;

            events?.fire('progressUpdate', { text: 'Building spatial tree…  —  0%', progress: 0 });
            console.warn(`[LCC2] building adaptive tree: N=${N.toLocaleString()}, treeDepth=${treeDepth}`);
            const adaptiveRoot = buildAdaptiveLcc2Tree(xs, ys, zs, N, treeDepth, boundingBox, SPLIT_TARGET);
            assignAdaptiveNodeIds(adaptiveRoot);
            const depth1Count = adaptiveRoot.child?.length ?? 0;
            // Count total nodes for logging.
            let totalNodes = 0;
            const countNodes = (n: AdaptiveNode) => {
                totalNodes++;
                if (n.child) for (const c of n.child) countNodes(c);
            };
            if (adaptiveRoot.child) for (const c of adaptiveRoot.child) countNodes(c);
            console.warn(`[LCC2] adaptive tree done: ${depth1Count} root children, ${totalNodes} total nodes`);

            // nodeRefs maps each AdaptiveNode to its {name, start, count} chunk ref.
            const nodeRefs = new Map<AdaptiveNode, Lcc2NodeRef>();
            const lodSplatsByLevel = new Array(treeDepth).fill(0);
            // Track chunk index per LOD level; k=0=finest.
            const chunkIdxByLevel = new Array(treeDepth).fill(0);
            const srcCols = dataTable.columns;
            const numCols = srcCols.length;

            // LOD 精细度动态采样率：LOD0 (finest) = 100%, LOD(L-1) (coarsest) = 10%,
            // 中间线性递减。保证各级别精细度严格递减且不重复（用户要求：从 lod0
            // 开始依次递减，最小一级永远 10%）。L=1 时单 LOD = 100%。
            // 旧逻辑用 step=2^k 导致 100/50/25/12.5... 粗糙 LOD 数据过少。
            const lodRate = (k: number): number => (treeDepth === 1 ? 1 : 1 - 0.9 * k / (treeDepth - 1));

            // Pre-compute estimated total splats for weighted progress.
            let totalEstimatedSplats = 0;
            for (let D = 1; D <= treeDepth; ++D) {
                const k = treeDepth - D;
                totalEstimatedSplats += Math.ceil(N * lodRate(k));
            }
            let cumulativeSplats = 0;

            events?.fire('progressUpdate', { text: `Writing ${treeDepth} LOD levels (${totalEstimatedSplats.toLocaleString()} splats total)…  —  1%`, progress: 1 });

            // Encode coarsest (depth 1) → finest (depth treeDepth).
            for (let D = 1; D <= treeDepth; ++D) {
                const k = treeDepth - D;     // LOD level index (0=finest)
                const rate = lodRate(k);     // 动态采样率：100% → 10% 线性递减
                const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);

                // ---- Pass 1: count LOD samples per node (no subset allocation yet) ----
                const counts: number[] = new Array(nodesAtD.length);
                let levelTotal = 0;
                for (let i = 0; i < nodesAtD.length; ++i) {
                    const nodeLen = nodesAtD[i].finestIndices.length;
                    // rate=1 → 全量; rate=0.1 → 10%。至少 1 个点保证非空节点有数据。
                    const cnt = Math.max(1, Math.ceil(nodeLen * rate));
                    counts[i] = cnt;
                    levelTotal += cnt;
                }
                console.warn(`[LCC2] LOD${k}/depth${D}: ${nodesAtD.length} nodes, ${levelTotal.toLocaleString()} splats (rate=${(rate * 100).toFixed(1)}%)`);

                if (levelTotal === 0) {
                    continue;
                }

                // ---- Pass 2: write files in chunks <= SOG_CHUNK_TARGET ----
                // Double-buffered pipeline: while chunk N's writeSogInternal is
                // awaiting worker WebP encoding, the main thread concurrently
                // prepares chunk N+1's chunkTable (pure CPU column subset). Two
                // writeSogInternal calls never overlap (they share one cached
                // GPU device + backbuffer via createGpuDevice), but the CPU
                // gather/subset work is hidden behind the worker WebP time.
                // Peak memory = finest DataTable + current chunk + prefetched
                // next chunk (each ≤ SOG_CHUNK_TARGET splats).
                // Display offset by -1: k=0 (finest, 100%) is the pristine source
                // and not a decimated "LOD level", so it falls back to the raw
                // step name (currentExportLod=-1). Coarser levels k≥1 map to
                // LOD0..LOD(treeDepth-2), matching UE plugin's 0-based LOD indexing
                // where LOD0 is the first decimated level.
                currentExportLod = k - 1;
                lodExportProgress = Math.round(100 * (cumulativeSplats + levelTotal) / totalEstimatedSplats);

                // Build a chunk's chunkTable + identity indices (pure CPU, no I/O).
                // Returns null when no more nodes remain.
                const prepareChunk = (startNode: number) => {
                    let chunkSplatCount = 0;
                    let endNode = startNode;
                    while (endNode < nodesAtD.length) {
                        const nxt = chunkSplatCount + counts[endNode];
                        if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break; // don't split a node mid-way
                        chunkSplatCount = nxt;
                        endNode++;
                    }
                    if (chunkSplatCount === 0) return null;

                    // Uniformly sample `cnt` points from each node's finestIndices
                    // (idx = floor(j * nodeLen / cnt)) and concatenate
                    // [startNode..endNode-1]. rate=1 → 全量; rate=0.1 → 均匀取 10%.
                    const chunkIndices = new Uint32Array(chunkSplatCount);
                    let wi = 0;
                    for (let n = startNode; n < endNode; ++n) {
                        const finest = nodesAtD[n].finestIndices;
                        const cnt = counts[n];
                        const nodeLen = finest.length;
                        for (let j = 0; j < cnt; ++j) {
                            chunkIndices[wi++] = finest[Math.floor(j * nodeLen / cnt)];
                        }
                    }

                    // Column-by-column from source for memory safety.
                    const chunkCols: Column[] = new Array(numCols);
                    for (let ci = 0; ci < numCols; ++ci) {
                        const src = srcCols[ci].data as Float32Array;
                        const dst = new Float32Array(chunkSplatCount);
                        for (let i = 0; i < chunkSplatCount; ++i) dst[i] = src[chunkIndices[i]];
                        chunkCols[ci] = new Column(srcCols[ci].name, dst);
                    }
                    const chunkTable = new DataTable(chunkCols, dataTable.transform);

                    const identity = new Uint32Array(chunkSplatCount);
                    for (let i = 0; i < chunkSplatCount; ++i) identity[i] = i;

                    const chunkIdx = chunkIdxByLevel[k]++;
                    const firstNode = nodesAtD[startNode];
                    const lodFileName = `${firstNode.id}.sog`;
                    const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                    console.warn(`[LCC2] LOD${k}/chunk${chunkIdx}: nodes[${startNode}..${endNode - 1}] ${chunkSplatCount.toLocaleString()} splats → ${chunkPath}`);

                    return { startNode, endNode, chunkTable, identity, lodFileName, chunkPath, chunkIdx };
                };

                // Record splatFiles entry + per-node refs after a chunk's write completes.
                const finalizeChunk = (info: { startNode: number; endNode: number; lodFileName: string; chunkIdx: number }) => {
                    const fileIdx = splatFiles.length;
                    splatFiles.push(`data/3dgs/${info.lodFileName}`);
                    console.warn(`[LCC2] LOD${k}/chunk${info.chunkIdx}: done`);
                    let nodeOffset = 0;
                    for (let n = info.startNode; n < info.endNode; ++n) {
                        nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: counts[n] });
                        lodSplatsByLevel[k] += counts[n];
                        nodeOffset += counts[n];
                    }
                    (globalThis as { gc?: () => void }).gc?.();
                };

                // Pipeline: prefetch chunk N+1's chunkTable while chunk N's
                // writeSogInternal awaits worker WebP. writeSogInternal returns
                // a Promise after its synchronous gather/k-means setup reaches
                // the first worker await; the synchronous prepareChunk() for the
                // next chunk then runs concurrently with N's worker WebP.
                let cursor = 0;
                let nextInfo = prepareChunk(cursor);
                while (nextInfo) {
                    cursor = nextInfo.endNode;

                    // Start chunk N's write (returns Promise; runs gather/k-means
                    // then awaits worker WebP).
                    const writePromise = writeSogInternal({
                        filename: nextInfo.chunkPath,
                        dataTable: nextInfo.chunkTable,
                        bundle: true,
                        iterations,
                        createDevice: createGpuDevice,
                        indices: nextInfo.identity
                    }, fs);
                    const currentInfo = nextInfo;

                    // Prefetch chunk N+1's chunkTable on the main thread while
                    // chunk N's worker WebP encodes. This is pure CPU column
                    // copy — does not touch the shared GPU device.
                    nextInfo = prepareChunk(cursor);

                    // Await chunk N complete before starting chunk N+1's write
                    // (shared GPU device — no concurrent writeSogInternal).
                    await writePromise;
                    finalizeChunk(currentInfo);
                }

                cumulativeSplats += levelTotal;
                const progressPercent = Math.round(100 * cumulativeSplats / totalEstimatedSplats);
                events?.fire('progressUpdate', {
                    text: `LOD ${k} (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                    progress: progressPercent
                });
            }

            // Finest-first order matching the source/reference convention
            // (lodSplats[0] = LOD0 = finest = largest count). The UE plugin
            // indexes lodSplats by LOD level; reversed order mislabels levels.
            for (let k = 0; k < treeDepth; ++k) lodSplats.push(lodSplatsByLevel[k]);

            // Assemble the nested tree JSON from the adaptive tree.
            root = emitAdaptiveTreeJson(adaptiveRoot, nodeRefs, splatFiles);
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    } finally {
        events?.fire('progressEnd');
    }

    const totalSplats = lodSplats.reduce((a, b) => a + b, 0);

    const meta = {
        version: '0.0.3',
        name: name || 'ReSplat Lcc2 Splats',
        description: 'ReSplat exported LCC2 splats',
        guid,
        source: 'resplat',
        dataType: 'Editor',
        epsg: 0,
        offset: [0, 0, 0],
        shift: [0, 0, 0],
        scale: [1, 1, 1],
        fileType,
        totalSplats,
        lodSplats,
        totalLevels: lodLevels,
        splatType,
        virtualLoD: null as null,
        env: null as null,
        splatExtraAttributes: null as null,
        renderingHints: {
            renderMethod: 'splatting',
            renderMethodVariant: 'ewa',
            sortingMethod: 'depth',
            cameraModel: 'pinhole',
            colorSpace: 'srgb_rec709_display'
        },
        root
    };

    // Write the metadata file
    const jsonWriter = await fs.createWriter(`${name}/${name}.lcc2`);
    await jsonWriter.write(new TextEncoder().encode(JSON.stringify(meta, null, 2)));
    await jsonWriter.close();

    // ZIP fallback: if writing into a MemoryFileSystem (browser without
    // showDirectoryPicker), package the in-memory tree into a ZIP download.
    if (fs instanceof MemoryFileSystem) {
        const downloadFs = new BrowserFileSystem(`${name}.zip`);
        const zipWriter = await downloadFs.createWriter(`${name}.zip`);
        const zipFs = new ZipFileSystem(zipWriter);
        try {
            for (const [filename, data] of fs.results.entries()) {
                const writer = await zipFs.createWriter(filename);
                await writer.write(data);
                await writer.close();
            }
        } finally {
            await zipFs.close();
        }
    }
};

// Multi-LOD LCC2 export from a Splat with a LodEditLog. Unlike serializeLcc2
// (which sub-samples a single DataTable), this streams each LOD level from the
// original LCC file, replays deletions from the LodEditLog, and writes one
// .sog chunk per spatial-tree-node-group per LOD. The adaptive tree is built
// on the finest LOD (LOD 0); coarser LODs are binned into the same tree by
// AABB containment (tree descent per splat).
//
// Memory: peak = finest LOD + one cached coarser LOD. Each LOD is released
// before the next is loaded. GC is hinted between LODs.
//
// Requires splat.lodEditLog, splat.lccFilePath, splat.lccFileSystem, and
// splat.lodCounts.length >= 2. Falls back to serializeLcc2 otherwise.
const serializeLcc2FromLodLog = async (
    splat: Splat,
    options: Lcc2ExportOptions,
    fs: FileSystem
): Promise<void> => {
    const { name, serializeSettings } = options;
    const { iterations = 0, events } = serializeSettings;
    const splatType = options.splatType ?? '.sog';

    // Cap maxSHBands to the source file's actual SH bands. loadLodForExport
    // calls extractDataTable with these settings; if the user requests SH=3
    // (59 cols) but the source LCC2 is "portable" (SH=0, 14 cols), the 45
    // f_rest columns are zero-filled — wasting N×45×4 bytes and pushing peak
    // memory past the ~4 GB renderer limit. For LOD1 (17.8M splats) this is
    // 4.2 GB → "Array buffer allocation failed". Capping to source SH drops
    // LOD1 to 1.0 GB. The source's SH bands are the same across all LODs
    // (same file), so a single upfront detection is valid.
    const requestedSHBands = serializeSettings.maxSHBands ?? 3;
    const sourceMaxSHBands = calcSHBands(getVertexProperties(splat.splatData));
    const maxSHBands = Math.min(requestedSHBands, sourceMaxSHBands);
    if (maxSHBands < requestedSHBands) {
        console.warn(
            `[LCC2] Source SH=${sourceMaxSHBands} < requested SH=${requestedSHBands}; ` +
            `capping to SH=${maxSHBands} (avoids zero-fill column OOM in loadLodForExport)`
        );
    }
    const exportSettings: SerializeSettings = { ...serializeSettings, maxSHBands };

    const { lccFilePath, lccFileSystem, lodCounts, lodEditLog, currentLodIndex } = splat;
    if (!lccFilePath || !lccFileSystem || !lodEditLog) {
        throw new Error('[LCC2] serializeLcc2FromLodLog requires lccFilePath, lccFileSystem, and lodEditLog');
    }
    const numLods = lodCounts.length;
    if (numLods < 2) {
        throw new Error('[LCC2] serializeLcc2FromLodLog requires a multi-LOD source file (numLods >= 2)');
    }

    await fs.mkdir(`${name}/data/3dgs`);
    configureSplatTransform();

    // Show progress immediately — tree building + LOD loading happen before the
    // first writeSogInternal call (which is when createProgressRenderer normally
    // fires its first event). Without this the user sees nothing for ~minutes.
    events?.fire('progressStart', 'Exporting LCC2 (Multi-LOD)');
    events?.fire('progressUpdate', { text: 'Building spatial tree…  —  1%', progress: 1 });

    // Custom progress renderer: unlike createProgressRenderer, this does NOT
    // fire progressStart (dialog reset) or progressEnd (dialog hide) — we
    // manage the dialog lifecycle manually to prevent flickering between LOD
    // levels. Sub-step bar progress is also suppressed to avoid resetting the
    // cumulative percentage. Instead, we show "正在导出 LOD{N}" with the
    // current cumulative progress.
    let currentExportLod = -1;
    let lodExportProgress = 5;
    splatTransformLogger.setRenderer({
        handle: (event: LogEvent) => {
            switch (event.kind) {
                case 'scopeStart':
                    if (event.depth > 0) {
                        const label = currentExportLod >= 0 ? `正在导出 LOD${currentExportLod}` : event.name;
                        events?.fire('progressUpdate', { text: `${label}  —  ${lodExportProgress}%` });
                    }
                    // depth===0: suppress (would fire progressStart, resetting dialog)
                    break;
                case 'scopeEnd':
                    // Suppress — would fire progressEnd, hiding dialog between depths
                    break;
                case 'barStart':
                case 'barTick':
                case 'barEnd':
                    // Suppress sub-step progress — would reset cumulative percentage
                    break;
                case 'message':
                    if (event.level === 'error') console.error(event.text);
                    else if (event.level === 'warn') console.warn(event.text);
                    break;
            }
        }
    });

    // Load a LOD as a PLY-space DataTable (matching extractDataTable's output).
    // For the current LOD, reuse the Splat's data directly (deletions/transforms
    // already applied via EditHistory). For other LODs, stream-load from the
    // source file, apply deletions from LodEditLog, then extract via a mock Splat
    // that reuses the current Splat's entity/world-transform/color-grade.
    // Does NOT apply applyLcc2CoordinateTransform — the caller does that.
    const loadLodForExport = async (lodIndex: number, settings: SerializeSettings = exportSettings): Promise<DataTable> => {
        if (lodIndex === currentLodIndex) {
            return extractDataTable([splat], settings);
        }

        console.warn(`[LCC2] Loading LOD ${lodIndex} (${lodCounts[lodIndex].toLocaleString()} splats) from ${lccFilePath}`);
        const rawDataTable = await loadLodDataTable(lccFilePath, lccFileSystem, lodIndex);

        // Convert to GSplatData so extractDataTable's SingleSplat/SplatTransformCache
        // can read properties via getProp.
        const gsplatData = dataTableToGSplatData(rawDataTable);

        // Add 'state' property (all 0) for GaussianFilter's deleted/selected checks.
        if (!gsplatData.getProp('state')) {
            gsplatData.getElement('vertex').properties.push({
                type: 'uchar',
                name: 'state',
                storage: new Uint8Array(gsplatData.numSplats),
                byteSize: 1
            });
        }

        // Apply deletions from LodEditLog: mark deleted rows in the state array
        // so GaussianFilter (inside extractDataTable) filters them out.
        const state = gsplatData.getProp('state') as Uint8Array;
        const xs = gsplatData.getProp('x') as Float32Array;
        const ys = gsplatData.getProp('y') as Float32Array;
        const zs = gsplatData.getProp('z') as Float32Array;
        lodEditLog.applyDeletionsToState(state, xs, ys, zs, splat.worldTransform);

        // Mock Splat: reuse the current Splat's entity (world transform), color
        // grade properties, and provide a transformTexture whose getSource()
        // returns null so SplatTransformCache treats all splats as untransformed
        // (transformIndex = 0 → identity per-splat transform).
        const mockSplat = {
            splatData: gsplatData,
            numSplats: gsplatData.numSplats,
            transformTexture: { getSource: (): null => null },
            transformPalette: null,
            entity: splat.entity,
            tintClr: splat.tintClr,
            temperature: splat.temperature,
            saturation: splat.saturation,
            brightness: splat.brightness,
            blackPoint: splat.blackPoint,
            whitePoint: splat.whitePoint,
            transparency: splat.transparency
        } as unknown as Splat;

        return extractDataTable([mockSplat], settings);
    };

    // ---- Build spatial tree from the finest LOD (LOD 0) ----
    // Tree building only needs positions, not SH/color/opacity. Loading the
    // full 59-column DataTable (10.5M × 59 × 4B = 2.5GB) freezes the UI for
    // minutes. Use reduced maxSHBands=0 (14 columns) — the position transform
    // is identical regardless of SH level, so the spatial tree is correct.
    // Pass treeSettings explicitly so the file-load path also uses SH=0
    // (previously the currentLodIndex!==0 branch called loadLodForExport with
    // the full-SH exportSettings, wasting memory on columns the tree ignores).
    const treeSettings: SerializeSettings = { ...serializeSettings, maxSHBands: 0 };
    const treeLodForExport = (lodIndex: number): Promise<DataTable> => loadLodForExport(lodIndex, treeSettings);

    console.warn(`[LCC2] Building spatial tree from finest LOD (${lodCounts[0].toLocaleString()} splats, SH=0)`);
    const treeDataTable = await treeLodForExport(0);
    // H4: treeLodForExport's file-load path holds rawDataTable (LOD 0) + the
    // SH=0 extractDataTable result simultaneously. Hint GC to release
    // rawDataTable/gsplatData before the export loop allocates coarser LODs.
    (globalThis as { gc?: () => void }).gc?.();
    const boundingBox = applyLcc2CoordinateTransform(treeDataTable, maxSHBands);
    const treeN = treeDataTable.numRows;

    if (treeN === 0) {
        throw new Error('[LCC2] No splats to export after applying deletions to finest LOD');
    }

    const fileType = maxSHBands > 0 ? 'quality' : 'portable';
    const guid = crypto.randomUUID().replace(/-/g, '');

    const SOG_CHUNK_TARGET = 3_000_000;
    const SPLIT_TARGET = 500_000;
    const safeTreeDepth = Math.max(1, Math.ceil(Math.log2(treeN / SOG_CHUNK_TARGET)) - 2);
    const userLodLevels = Math.max(1, Math.min(20, options.lodLevels ?? 6));
    const treeDepth = Math.min(numLods, 20, Math.max(safeTreeDepth, userLodLevels));

    if (treeDepth < userLodLevels) {
        console.warn(`[LCC2] treeDepth capped at numLods=${numLods} (user requested ${userLodLevels})`);
    }
    if (treeDepth > userLodLevels) {
        console.warn(`[LCC2] auto-raised lodLevels ${userLodLevels}→${treeDepth} (N=${treeN.toLocaleString()})`);
    }

    const shFactor = maxSHBands > 0 ? 1 : 0.25;
    const estMin = Math.max(1, Math.round((treeN / 3.8e6) * 10 * shFactor));
    console.warn(`[LCC2] N=${treeN.toLocaleString()} treeDepth=${treeDepth} numLods=${numLods} SH=${maxSHBands} est ~${estMin} min`);

    // Pre-compute total estimated splats for weighted progress reporting.
    // Each LOD's work (load + SOG encode) is roughly proportional to its
    // splat count. Coarse LODs are tiny; the finest LOD dominates.
    let totalEstimatedSplats = 0;
    for (let D = 1; D <= treeDepth; ++D) {
        const k = treeDepth - D;
        const actualLod = Math.min(k, numLods - 1);
        totalEstimatedSplats += lodCounts[actualLod];
    }
    let cumulativeSplats = 0;

    const xs = treeDataTable.getColumnByName('x')!.data as Float32Array;
    const ys = treeDataTable.getColumnByName('y')!.data as Float32Array;
    const zs = treeDataTable.getColumnByName('z')!.data as Float32Array;

    console.warn(`[LCC2] building adaptive tree: N=${treeN.toLocaleString()}, treeDepth=${treeDepth}`);
    const adaptiveRoot = buildAdaptiveLcc2Tree(xs, ys, zs, treeN, treeDepth, boundingBox, SPLIT_TARGET);
    assignAdaptiveNodeIds(adaptiveRoot);
    const depth1Count = adaptiveRoot.child?.length ?? 0;
    console.warn(`[LCC2] adaptive tree done: ${depth1Count} root children`);
    events?.fire('progressUpdate', { text: `Writing ${treeDepth} LOD levels (${totalEstimatedSplats.toLocaleString()} splats total)…  —  5%`, progress: 5 });

    const splatFiles: string[] = [];
    const lodSplats: number[] = [];
    const nodeRefs = new Map<AdaptiveNode, Lcc2NodeRef>();
    const lodSplatsByLevel = new Array(treeDepth).fill(0);
    const chunkIdxByLevel = new Array(treeDepth).fill(0);

    // Cache the last loaded non-finest LOD to avoid reloading when consecutive
    // depths map to the same LOD (happens when numLods < treeDepth).
    let cachedLodIndex = -1;
    let cachedLodDataTable: DataTable | null = null;

    try {
        // ---- Per-depth chunk writing ----
        // Encode coarsest (depth 1) → finest (depth treeDepth).
        for (let D = 1; D <= treeDepth; ++D) {
            const k = treeDepth - D;          // LOD level index (0=finest)
            const actualLod = Math.min(k, numLods - 1);

            // Resolve the LOD DataTable for this depth.
            let lodDataTable: DataTable;
            if (actualLod === 0) {
                // The finest LOD: use the tree-building DataTable directly.
                // It already holds the finest-LOD data in LCC2 space, and
                // its `finestIndices` are valid row indices into it (same
                // extractDataTable path, same row order regardless of SH).
                //
                // The old code reloaded with full SH=3 settings when
                // currentLodIndex===0, but for large datasets (e.g. 35.7M
                // splats × 59 cols ≈ 8.4 GB) a single DataTable exceeds the
                // browser's ~4 GB ArrayBuffer limit → "Array buffer
                // allocation failed". The source file is typically
                // "portable" (SH=0) anyway, so SH=3 columns would be zeros.
                //
                // If the tree was built with SH=0 but the source actually
                // has SH data AND fits in memory, we could reload. For now,
                // prefer correctness (no OOM) over hypothetical quality gain.
                lodDataTable = treeDataTable;
            } else if (actualLod === cachedLodIndex && cachedLodDataTable) {
                lodDataTable = cachedLodDataTable;
            } else {
                // Release the previous cached LOD before loading a new one.
                if (cachedLodDataTable) {
                    cachedLodDataTable = null;
                    (globalThis as { gc?: () => void }).gc?.();
                }
                cachedLodDataTable = await loadLodForExport(actualLod);
                // H4: loadLodForExport's file-load path leaves rawDataTable +
                // gsplatData (the full source-LOD DataTable) GC-eligible but
                // not yet collected. Hint GC before the chunk-prep loop
                // allocates per-chunk Float32Array copies, so peak memory is
                // lodDataTable + treeDataTable instead of + rawDataTable.
                (globalThis as { gc?: () => void }).gc?.();
                cachedLodIndex = actualLod;
                lodDataTable = cachedLodDataTable;
            }

            // Convert to LCC2 space (in-place). Coarser LODs loaded from file
            // arrive in PLY space and need transform. The finest LOD is either
            // already in LCC2 space (treeDataTable, when currentLodIndex !== 0)
            // or freshly loaded + transformed above (when currentLodIndex === 0).
            if (actualLod !== 0) {
                applyLcc2CoordinateTransform(lodDataTable, maxSHBands);
            }

            const lodN = lodDataTable.numRows;
            const lodXs = lodDataTable.getColumnByName('x')!.data as Float32Array;
            const lodYs = lodDataTable.getColumnByName('y')!.data as Float32Array;
            const lodZs = lodDataTable.getColumnByName('z')!.data as Float32Array;
            const lodCols = lodDataTable.columns;
            const numCols = lodCols.length;

            const nodesAtD = collectNodesAtDepth(adaptiveRoot, D);

            // ---- Bin LOD splats into depth-D nodes ----
            // For the finest LOD (actualLod===0), the tree's `finestIndices` are
            // already valid indices into `lodDataTable` (same row order as the
            // tree-building DataTable — extractDataTable filters rows identically
            // regardless of SH settings). Use them directly — O(1) per node.
            // For coarser LODs, descend the tree per splat to find the containing
            // depth-D node by AABB. O(D × max_children) per splat.
            const nodeSubsets: (Uint32Array | null)[] = new Array(nodesAtD.length).fill(null);
            const counts: number[] = new Array(nodesAtD.length).fill(0);
            let levelTotal = 0;

            if (actualLod === 0) {
                // Finest LOD: finestIndices ARE the LOD-0 indices (step = 1).
                for (let i = 0; i < nodesAtD.length; ++i) {
                    const sub = nodesAtD[i].finestIndices;
                    nodeSubsets[i] = sub;
                    counts[i] = sub.length;
                    levelTotal += sub.length;
                }
            } else {
                // Coarser LOD: bin by AABB descent.
                // Map node → index for O(1) lookup during descent.
                const nodeIndex = new Map<AdaptiveNode, number>();
                for (let i = 0; i < nodesAtD.length; ++i) nodeIndex.set(nodesAtD[i], i);

                // Pass 1: count splats per node via tree descent.
                for (let i = 0; i < lodN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lodXs[i], lodYs[i], lodZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined) counts[idx]++;
                    }
                }
                // Allocate subset arrays.
                for (let i = 0; i < nodesAtD.length; ++i) {
                    if (counts[i] > 0) {
                        nodeSubsets[i] = new Uint32Array(counts[i]);
                        levelTotal += counts[i];
                    }
                }
                // Pass 2: fill subsets.
                const fill = new Uint32Array(nodesAtD.length);
                for (let i = 0; i < lodN; ++i) {
                    const node = findAdaptiveNodeAtDepth(adaptiveRoot, lodXs[i], lodYs[i], lodZs[i], D);
                    if (node) {
                        const idx = nodeIndex.get(node);
                        if (idx !== undefined && nodeSubsets[idx]) {
                            nodeSubsets[idx]![fill[idx]++] = i;
                        }
                    }
                }
            }
            console.warn(`[LCC2] LOD${k}/depth${D}: ${nodesAtD.length} nodes, ${levelTotal.toLocaleString()} splats`);

            if (levelTotal === 0) {
                continue;
            }

            // Tell the progress renderer which LOD is being written. Pre-
            // compute the expected final progress so "正在导出 LOD{N}" shows
            // the destination percentage during SOG encoding.
            // Display offset by -1: k=0 (finest, 100%) is the pristine source
            // and not a decimated "LOD level", so it falls back to the raw
            // step name (currentExportLod=-1). Coarser levels k≥1 map to
            // LOD0..LOD(treeDepth-2), matching UE plugin's 0-based LOD indexing.
            currentExportLod = k - 1;
            lodExportProgress = 5 + Math.round(95 * (cumulativeSplats + levelTotal) / totalEstimatedSplats);

            // ---- Write files in chunks <= SOG_CHUNK_TARGET ----
            // Double-buffered pipeline: while chunk N's writeSogInternal awaits
            // worker WebP encoding, the main thread concurrently prepares chunk
            // N+1's chunkTable (pure CPU column subset). Two writeSogInternal
            // calls never overlap (shared cached GPU device). See serializeLcc2
            // for the same pattern. Peak memory = lodDataTable + current chunk
            // + prefetched next chunk.
            const prepareChunk = (startNode: number) => {
                let chunkSplatCount = 0;
                let endNode = startNode;
                while (endNode < nodesAtD.length) {
                    const nxt = chunkSplatCount + counts[endNode];
                    if (nxt > 0 && nxt > SOG_CHUNK_TARGET) break;
                    chunkSplatCount = nxt;
                    endNode++;
                }
                if (chunkSplatCount === 0) return null;

                const chunkIndices = new Uint32Array(chunkSplatCount);
                let wi = 0;
                for (let n = startNode; n < endNode; ++n) {
                    const sub = nodeSubsets[n];
                    if (sub) {
                        chunkIndices.set(sub, wi);
                        wi += counts[n];
                    }
                }

                // Column-by-column from source for memory safety.
                const chunkCols: Column[] = new Array(numCols);
                for (let ci = 0; ci < numCols; ++ci) {
                    const src = lodCols[ci].data as Float32Array;
                    const dst = new Float32Array(chunkSplatCount);
                    for (let i = 0; i < chunkSplatCount; ++i) dst[i] = src[chunkIndices[i]];
                    chunkCols[ci] = new Column(lodCols[ci].name, dst);
                }
                const chunkTable = new DataTable(chunkCols, lodDataTable.transform);

                const identity = new Uint32Array(chunkSplatCount);
                for (let i = 0; i < chunkSplatCount; ++i) identity[i] = i;

                const chunkIdx = chunkIdxByLevel[k]++;
                const firstNode = nodesAtD[startNode];
                const lodFileName = `${firstNode.id}.sog`;
                const chunkPath = `${name}/data/3dgs/${lodFileName}`;
                console.warn(`[LCC2] LOD${k}/chunk${chunkIdx}: nodes[${startNode}..${endNode - 1}] ${chunkSplatCount.toLocaleString()} splats → ${chunkPath}`);

                return { startNode, endNode, chunkTable, identity, lodFileName, chunkPath, chunkIdx };
            };

            const finalizeChunk = (info: { startNode: number; endNode: number; lodFileName: string; chunkIdx: number }) => {
                const fileIdx = splatFiles.length;
                splatFiles.push(`data/3dgs/${info.lodFileName}`);
                console.warn(`[LCC2] LOD${k}/chunk${info.chunkIdx}: done`);

                let nodeOffset = 0;
                for (let n = info.startNode; n < info.endNode; ++n) {
                    if (counts[n] > 0) {
                        nodeRefs.set(nodesAtD[n], { name: fileIdx, start: nodeOffset, count: counts[n] });
                        lodSplatsByLevel[k] += counts[n];
                    }
                    nodeOffset += counts[n];
                }
                (globalThis as { gc?: () => void }).gc?.();
            };

            // Pipeline: prefetch chunk N+1's chunkTable while chunk N's
            // writeSogInternal awaits worker WebP.
            let cursor = 0;
            let nextInfo = prepareChunk(cursor);
            while (nextInfo) {
                cursor = nextInfo.endNode;

                const writePromise = writeSogInternal({
                    filename: nextInfo.chunkPath,
                    dataTable: nextInfo.chunkTable,
                    bundle: true,
                    iterations,
                    createDevice: createGpuDevice,
                    indices: nextInfo.identity
                }, fs);
                const currentInfo = nextInfo;

                // Prefetch chunk N+1's chunkTable (pure CPU) while chunk N's
                // worker WebP encodes.
                nextInfo = prepareChunk(cursor);

                // Await chunk N complete before starting chunk N+1's write
                // (shared GPU device — no concurrent writeSogInternal).
                await writePromise;
                finalizeChunk(currentInfo);
            }

            cumulativeSplats += levelTotal;
            const progressPercent = 5 + Math.round(95 * cumulativeSplats / totalEstimatedSplats);
            events?.fire('progressUpdate', {
                text: `LOD ${k} (${D}/${treeDepth}): ${levelTotal.toLocaleString()} splats  —  ${progressPercent}%`,
                progress: progressPercent
            });
            lodExportProgress = progressPercent;
        }

        // Release cached LOD data.
        if (cachedLodDataTable) {
            cachedLodDataTable = null;
            (globalThis as { gc?: () => void }).gc?.();
        }

        // Finest-first order matching the source/reference convention
        // (lodSplats[0] = LOD0 = finest = largest count). The UE plugin
        // indexes lodSplats by LOD level; reversed order mislabels levels.
        for (let k = 0; k < treeDepth; ++k) lodSplats.push(lodSplatsByLevel[k]);

        // Assemble the nested tree JSON from the adaptive tree.
        const root = emitAdaptiveTreeJson(adaptiveRoot, nodeRefs, splatFiles);

        const totalSplats = lodSplats.reduce((a, b) => a + b, 0);

        const meta = {
            version: '0.0.3',
            name: name || 'ReSplat Lcc2 Splats',
            description: 'ReSplat exported LCC2 splats (multi-LOD from LodEditLog)',
            guid,
            source: 'resplat',
            dataType: 'Editor',
            epsg: 0,
            offset: [0, 0, 0],
            shift: [0, 0, 0],
            scale: [1, 1, 1],
            fileType,
            totalSplats,
            lodSplats,
            totalLevels: treeDepth,
            splatType,
            virtualLoD: null as null,
            env: null as null,
            splatExtraAttributes: null as null,
            renderingHints: {
                renderMethod: 'splatting',
                renderMethodVariant: 'ewa',
                sortingMethod: 'depth',
                cameraModel: 'pinhole',
                colorSpace: 'srgb_rec709_display'
            },
            root
        };

        // Write the metadata file
        const jsonWriter = await fs.createWriter(`${name}/${name}.lcc2`);
        await jsonWriter.write(new TextEncoder().encode(JSON.stringify(meta, null, 2)));
        await jsonWriter.close();

        // ZIP fallback (same as serializeLcc2).
        if (fs instanceof MemoryFileSystem) {
            const downloadFs = new BrowserFileSystem(`${name}.zip`);
            const zipWriter = await downloadFs.createWriter(`${name}.zip`);
            const zipFs = new ZipFileSystem(zipWriter);
            try {
                for (const [filename, data] of fs.results.entries()) {
                    const writer = await zipFs.createWriter(filename);
                    await writer.write(data);
                    await writer.close();
                }
            } finally {
                await zipFs.close();
            }
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    } finally {
        events?.fire('progressEnd');
    }
};

export {
    Writer,
    serializePly,
    serializePlyToWriter,
    serializePlyCompressed,
    serializeStandardPly,
    serializeSplat,
    serializeSog,
    serializeLcc2,
    serializeLcc2FromLodLog,
    serializeViewer,
    AnimTrack,
    CameraPose,
    Camera,
    Annotation,
    PostEffectSettings,
    defaultPostEffectSettings,
    ExperienceSettings,
    SerializeSettings,
    SogSettings,
    Lcc2ExportOptions,
    ViewerExportSettings,
    SingleSplat,
    SplatTransformCache
};
